import {ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {Column, ColumnState, GridOptions, GridReadyEvent} from "ag-grid-community";
import {
    buildEsStrikeGroup,
    getColumnDefs,
    getDaysOfWeekString,
    getEsQtyTotalColumn,
    getHedgeColumn,
    getHedgeExpirationsString,
    getHedgeMatrixGridOptions, syncCompanionRows, Width_Fit3digit, Width_Fit6digit, Width_FitDate
} from "./hedge-matrix-grid-options";
import {HedgePositionsService} from "../positions-section/hedge-positions/hedge-positions.service";
import {ApgPortfolio} from "../../adjustment-pricing-grid/model/ApgPortfolio";
import {
    defaultCurrencyFormatter,
    DetectMethodChanges,
    DxValueChanged,
    findHCF,
    getStrikeStep,
    isFuturesTicker,
    isValidNumber,
    isVoid,
    jsonCloneObject,
    makeDayOfWeekDate, makeFullExpirationDate,
    makeGuiFriendlyExpirationDate,
    makeGuiFriendlyExpirationDateDte,
    makeFuturesTosDesktopLine,
    makePortfolioKey,
    snapToStrikeStep, isVoidObject
} from "../../utils";
import {TradingInstrument} from "../../trading-instruments/trading-instrument.class";
import {LastQuoteCacheService} from "../../last-quote-cache.service";
import {OptionsChainService} from "../../option-chains.service";
import {ToastrService} from "ngx-toastr";
import {MessageBusService} from "../../message-bus.service";
import * as Enumerable from "linq";
import {HedgeMatrixDataServiceFactory} from "./hedge-matrix-data-service-factory.service";
import {ApgDataService} from "../../adjustment-pricing-grid/services/apg-data.service";
import {parseOptionTicker} from "../../options-common/options.model";
import {TradingInstrumentsService} from "../../trading-instruments/trading-instruments-service.interface";
import {OptionExpirationDescriptor} from "../../shell-communication/shell-dto-protocol";
import {AtmStrikeService} from "../../common-services/atm-strike-service/atm-strike.service";
import {HedgesPricingService} from "../../package-comparison/services/hedges-pricing.service";
import {UserSettingsService} from "../../user-settings.service";
import {animationFrameScheduler, Subject, Subscription} from "rxjs";
import {
    HgHedgeMatrixHedgesSelectorComponent
} from "./hg-hedge-matrix-hedges-selector/hg-hedge-matrix-hedges-selector.component";
import {isNullOrUndefined} from "util";
import {HedgeData} from "./hedge-data";
import {ComboHighlightedItem, ComboHighlightedUIMessage} from "../../ui-messages/ui-messages";
import {TimeInForce} from "../../trading-model/time-in-force.enum";
import {OrderType} from "../../trading-model/order-type.enum";
import {PortfolioItemType} from "../../portfolios/portfolios.model";
import {MarketSide} from "../../trading-model/market-side.enum";
import {ClipboardService, ETS_CLIPBOARD_LEGS_KEY} from "../../clipboard.service";
import {DateTime} from "luxon-business-days";
import {auditTime, debounceTime, filter} from "rxjs/operators";
import {HedgeMatrixSlideHedgeDialog} from "./slide-hedge/slide-hedge-dialog.component";
import {HedgePocketComponent} from "./hege-pocket/hedge-pocket.component";
import {OptionPricingGridTemplatesService} from "../../options-pricing-grid/option-pricing-grid-templates.service";
import {HedgeMatrixCellData} from "./hedge-matrix-cell.data";
import {findOffsetExpiration, HedgeMatrixDataService} from "./hedge-matrix-data.service";
import {
    OrdersVerificationDialogComponent
} from "../../options-pricing-grid/orders-verification-dialog/orders-verification-dialog.component";
import {
    OrdersVerificationDialogConfig
} from "../../options-pricing-grid/orders-verification-dialog/orders-verification-dialog.config";
import {ExpectedMoveService} from "../../expected-move/expected-move.service";
import {AppSettingsUpdatedMessageTopic} from "../../app-settings/application-settings.service";
import {OtherLegWrapper} from "./punch-pad/hg-matrix-punch-pad.component";
import {formatHedge, HedgePositionPick} from './export-to-gpt'
import {AccessControlService} from "../../access-control-service.class";
import {ApplyResult, CoPilotBookHedge} from "../copilot/copilot-facade";
import {Leg, PackageCard} from "../copilot/copilot-domain";
import {CoPilotPositionDto} from "../copilot/copilot-operations";
import {TradingInstrumentDisplayNameService} from "../../trading-instruments/trading-instrument-display-name.service";
import {
    GroupMetadata,
    PricingStepsPopupComponent,
    TransactionLeg
} from "../pricing-steps-popup/pricing-steps-popup.component";
import {HedgeMatrixTransLeg} from "./hedge-matrix-trans.leg";
import {HgUnderlyingPickerComponent} from "./underlying-picker/hg-underlying-picker.component";
import {
    buildHedgeColId, buildHedgeGroupId, buildPnlExpirationColId, buildPnlTotalColId,
    buildPricingColId, buildQtyTotalColId, buildSpacerColId, buildStrikeColId,
    getHedgeIdFromColId, isHedgeColumn, isPricingColumn, isQtyTotalColumn,
    migrateGridState, parseColId
} from "./hedge-matrix-col-id";
import {isEsHedgeableUnderlying} from "../es-hedging.util";
import {HEDGE_BOOK_RESET_TOPIC, HedgeBookResetPayload} from "../hg-hedge-book-reset.message";
import {HedgeMatrixUndoService} from "./hedge-matrix-undo.service";
import {ShellTodayService} from '../../shell-clock/shell-today.service';

const ShowPositionsKey = 'hg.matrix.show-positions';
const StrikesNoKey = 'hg.matrix.strikes-no';
const StrikeStepKey = 'hg.matrix.strike-step';
const CenterStrikeKey = 'hg.matrix.center-strike';
const EsHedgingModeKey = 'hg.matrix.es-hedging-mode';

type GridState = {
    colState?: any[];
    groupState?: any[];
    expirationPnlNormalizedState?: any[];
}

export interface HedgeMatrixRow {
    rowId?: string;
    strike?: number;
    otm?: number;
    rowSpan?: number;
    isPinned?: boolean;
    isHedgePrice?: boolean;
    isExpiration?: boolean;
    isDayOfWeek?: boolean;
    isMultiExpiration?: boolean;
}

export interface ExpirationDescriptor {
    side: 'Call' | 'Put';
    expiration: string;
    expirationFriendly: string;
    expirationFull: string;
    dayOfWeek: string;
    dayOfWeekShort: string;
    dte?: string;
    expirationTicker?: string;
    underlyingTicker?: string;
}

const FIT_WIDTH_COLUMNS_BTN_TEXT = 'Fit-Width Columns';
const OPT_WIDTH_COLUMNS_BTN_TEXT = 'Min-Width Columns';

@Component({
    selector: 'ets-hg-hedge-matrix',
    templateUrl: 'hg-hedge-matrix.component.html',
    styleUrls: ['hg-hedge-matrix.component.scss']
})

export class HgHedgeMatrixComponent implements OnInit {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _messageBus: MessageBusService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _optionsChainService: OptionsChainService,
        private readonly _toastr: ToastrService,
        private readonly _hedgeMatrixDataServiceFactory: HedgeMatrixDataServiceFactory,
        private readonly _apgDataService: ApgDataService,
        private readonly _tiService: TradingInstrumentsService,
        private readonly _atmStrikeService: AtmStrikeService,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _userSettingsService: UserSettingsService,
        private readonly _clipboardService: ClipboardService,
        private readonly _opgTemplatesService: OptionPricingGridTemplatesService,
        private readonly _expectedMoveService: ExpectedMoveService,
        private readonly _accessControlService: AccessControlService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService,
        private readonly _shellToday: ShellTodayService
    ) {
        const svc = new HedgesPricingService(
            this._hedgePositionsService,
            this._lastQuoteCache,
            this._messageBus,
            this._apgDataService,
        );
        this._hedgesPricingService = svc;
    }

    private readonly _hedgesPricingService: HedgesPricingService;
    private readonly _subscriptions: Subscription[] = [];

    private _hedgeMatrixDataService: HedgeMatrixDataService;

    private _isRestoringGridState = false;
    private _pendingInsertAtColId: string = null;
    private _pendingInsertSide: 'left' | 'right' = 'left';
    private _acquiredHedgeIdMap = new Map<string, string>(); // oldLocalId → hedgeName
    private _saveGridState$ = new Subject<void>();

    // CR-20260901-005 — Ctrl+Z/Ctrl+Y over quantity-changing gestures. Lifetime = one matrix
    // session: every boundary (portfolio switch, reload, hedge add/remove, CoPilot preview
    // transition) clears it via clearMatrixUndoHistory.
    private readonly _matrixUndo = new HedgeMatrixUndoService();

    get expectedMoveService(): ExpectedMoveService {
        return this._expectedMoveService;
    }

    @ViewChild(HgHedgeMatrixHedgesSelectorComponent)
    hedgesSelectorCmp: HgHedgeMatrixHedgesSelectorComponent;

    @ViewChild(HedgeMatrixSlideHedgeDialog)
    slideHedgeDialog: HedgeMatrixSlideHedgeDialog;

    @ViewChild(HedgePocketComponent)
    hedgePocket: HedgePocketComponent;

    @ViewChild(OrdersVerificationDialogComponent)
    orderVerificationDialog: OrdersVerificationDialogComponent;

    @ViewChild(PricingStepsPopupComponent)
    pricingStepsPopup: PricingStepsPopupComponent;

    @ViewChild(HgUnderlyingPickerComponent)
    underlyingPicker: HgUnderlyingPickerComponent;

    @Output() acquireRequested = new EventEmitter<ComboHighlightedUIMessage[]>();

    isLoading = false;

    theGrid: GridReadyEvent;

    gridOptions: GridOptions;

    selectedPortfolio: ApgPortfolio;

    portfolioDefaultQty = 1;

    centerStrike: number;

    tradingInstrument: TradingInstrument;

    strikeSteps = [];

    strikeStep = 5;

    numOfStrikes: number = 20;

    showPortfolioPositions = false;

    esHedgingEnabled = false;

    controlPanelExpanded = true;

    zoomLevel = 1;

    headersList = [
        'All',
        'Price Only',
        'Hide'
    ];

    headers: 'All' | 'Price Only' | 'Hide' = 'All';

    expirationForAtmSelector: OptionExpirationDescriptor;

    expirationPnlShowNormalized: string[] = [];

    private _sizeColumnsButtonText = OPT_WIDTH_COLUMNS_BTN_TEXT;
    get sizeColumnsButtonText(): string {
        return this._sizeColumnsButtonText;
    }

    get hedgesPricingService(): HedgesPricingService {
        return this._hedgesPricingService;
    }

    get hedgeMatrixDataService(): HedgeMatrixDataService {
        return this._hedgeMatrixDataService;
    }

    get lastQuoteCache(): LastQuoteCacheService {
        return this._lastQuoteCache;
    }

    get userSettingsService(): UserSettingsService {
        return this._userSettingsService;
    }

    get optionChainService(): OptionsChainService {
        return this._optionsChainService;
    }

    ngOnInit() {

        this.restoreValues();

        this._hedgesPricingService.init();

        this.gridOptions = getHedgeMatrixGridOptions(this);
        ['Qg.ChainsChangingMessage', 'Qg.ChainsUpdatedMessage'].forEach(topic =>
            this._subscriptions.push(this._messageBus.of<any>(topic).subscribe(msg => this.onOptionChainsChanged(msg.payload))));
        this._subscriptions.push(this._shellToday.changed$.subscribe(() => {
            if (this._hedgeMatrixDataService && !this._isRestoringGridState) {
                this.fillReferenceLists(); this.onChange();
            }
        }));

        this._subscriptions.push(
            this._saveGridState$.pipe(
                debounceTime(500)
            ).subscribe(() => this.saveGridState())
        );

        this._subscriptions.push(
            this._messageBus.of<{ portfolio: ApgPortfolio }>('Hg.RefreshQuotes')
                .pipe(
                    debounceTime(250)
                )
                .subscribe(() => this.onChange())
        );

        this._subscriptions.push(
            this._messageBus.of<{ portfolio: ApgPortfolio }>('Hg.HedgePositionsChanged')
                .pipe(
                    filter(x => {
                        const pfKey0 = makePortfolioKey(x.payload.portfolio);
                        const pfKey1 = makePortfolioKey(this.selectedPortfolio);
                        return pfKey0 === pfKey1;
                    })
                )
                .subscribe(() => this.reloadData())
        );

        this._subscriptions.push(
            this._messageBus.of<{ portfolio: ApgPortfolio }>('Hg.HedgesCleared')
                .pipe(
                    filter(x => {
                        const pfKey0 = makePortfolioKey(x.payload.portfolio);
                        const pfKey1 = makePortfolioKey(this.selectedPortfolio);
                        return pfKey0 === pfKey1;
                    })
                )
                .subscribe(() => {
                    this.reloadData();
                })
        );

        this._subscriptions.push(
            this._messageBus.of<any>('ExpectedMoveRangeChanged')
                .subscribe(() => {
                    if (!this.theGrid) {
                        return;
                    }
                    this.theGrid.api.refreshCells({force: true});
                })
        );
        this._subscriptions.push(this._messageBus.of(AppSettingsUpdatedMessageTopic)
            .subscribe(() => {
                this.theGrid?.api.refreshCells({force: true});
            }));
    }

    ngOnDestroy() {
        this._subscriptions.forEach(x => x.unsubscribe());
    }


    @DetectMethodChanges({isAsync: true})
    async onGridReady(args: GridReadyEvent) {

        this.theGrid = args;

        const subj: Subject<any> = this.gridOptions.context.subj;

        if (subj) {
            subj.pipe(
                auditTime(0, animationFrameScheduler),
            ).subscribe(() => {
                this.theGrid.api.refreshCells({force: false});
            });
        }

    }

    private getRows(): HedgeMatrixRow[] {

        const strikesToShow = this.getStrikesToShow();

        const hedges = Enumerable.from(this.hedgeMatrixDataService.getHedges());

        const doubleStrikes = hedges.selectMany(hedge => {

            const cellData = this.hedgeMatrixDataService
                .getCellDataForHedge(hedge.id);

            const multiExpGroups = Enumerable.from(cellData)
                .groupBy(x => x.strike)
                .where(x => x.count() > 1);

            if (multiExpGroups.count() > 0) {
                return multiExpGroups.select(x => x.key()).toArray();
            }
            return [];
        });

        const rows = strikesToShow.flatMap(x => {

            const otm = x - this.centerStrike;

            const rowSpan = doubleStrikes.indexOf(x) >= 0 ? 2 : 1;

            const rowSpanRows = Enumerable.range(0, rowSpan)
                .select(((y, ix) => {
                    return {
                        rowId: ix === 0 ? x + '' : x + '-' + ix,
                        otm,
                        strike: x,
                        rowSpan: ix === 0 ? rowSpan : 1,
                        isMultiExpiration: rowSpan > 1
                    } as HedgeMatrixRow
                }))
                .orderByDescending(z => z.rowSpan)
                .toArray();

            return rowSpanRows;
        });

        return rows;
    }

    private getStrikesToShow(): number[] {

        let centerStrike = this.centerStrike;

        if (isVoid(centerStrike)) {

            centerStrike = this._atmStrikeService.getCurrentAtm(this.tradingInstrument.ticker);

            if (isVoid(centerStrike)) {
                this._toastr.warning('Cannot Determine Correct Market ATM. Check Quotes Provider');
                return [];
            }
        }

        const atmStrike = [this.getAtmStrike()].filter(x => isValidNumber(x));

        const numOfStrikes = this.numOfStrikes;
        const upperBound = centerStrike + numOfStrikes * this.strikeStep;
        const lowerBound = centerStrike - numOfStrikes * this.strikeStep;

        const up = Enumerable
            .rangeTo(centerStrike + this.strikeStep, upperBound, this.strikeStep);

        const down = Enumerable
            .rangeTo(lowerBound, centerStrike - this.strikeStep, this.strikeStep);

        const hedgeStrikes = this.hedgeMatrixDataService.getLegStrikes();

        let portfolioPositionStrikes = this.hedgeMatrixDataService.getPortfolioPositionStrikes();

        const strikes = Enumerable.from([centerStrike])
            .concat(atmStrike)
            .concat(up)
            .concat(down)
            .concat(hedgeStrikes)
            .concat(portfolioPositionStrikes)
            .distinct()
            .orderByDescending(x => x)
            .toArray();

        return strikes;
    }

    @DetectMethodChanges({isAsync: true})
    async onPortfolioSelected(args: ApgPortfolio): Promise<void> {

        await this._hedgesPricingService.subscribe(args);

        this._isRestoringGridState = true;

        try {
            this.theGrid.api.setColumnDefs([]);

            this.theGrid.api.setRowData([]);

            this.selectedPortfolio = args;

            this.restorePerPortfolioMatrixSettings();

            this.portfolioDefaultQty = await this._apgDataService.getDefaultQtyForPortfolio(args);

            this.expirationPnlShowNormalized = [];

            const ul = await this.getPortfolioUnderlying(args);
            await this._shellToday.refresh(ul);

            const instrumentStep = getStrikeStep(ul);

            this.strikeSteps = Enumerable
                .rangeTo(1, 50)
                .select(x => x * instrumentStep)
                .toArray();

            this.strikeStep = snapToStrikeStep(this.strikeStep, ul);
            if (!this.strikeSteps.includes(this.strikeStep)) {
                this.strikeStep = this.strikeSteps[0];
            }

            this._atmStrikeService.watch(ul);

            this.tradingInstrument = this._tiService.getInstrumentByTicker(ul);

            this._hedgeMatrixDataService = this._hedgeMatrixDataServiceFactory.getPortfolioDataService(
                this.selectedPortfolio
            );
            await this.hedgeMatrixDataService.onPortfolioSelected();

            this.restoreEsHedgingMode();

            this.expirationForAtmSelector = this.hedgeMatrixDataService.getNearestExpiration();

            this.fillReferenceLists();

            if (isVoid(this.centerStrike)) {
                this.centerStrike = this._atmStrikeService.getCurrentAtm(ul);
            }

            const columnDefs = getColumnDefs(this);

            this.applyColumnDefs(columnDefs);
        } finally {
            this._isRestoringGridState = false;
        }

        this.hedgeMatrixDataService.getHedges().filter(x => !x.isNew).forEach(x => {
            const col = this.theGrid.columnApi.getColumn(x.id);
            if (!col) {
                return;
            }
            this.modifyHege(col, false);
        });

        const rows = this.getRows();

        this.hedgeMatrixDataService.calculatePnls(
            rows.map(x => x.strike)
        );

        this.theGrid.api.setRowData(rows);

        const pinned = this.getPinnedRows();

        this.theGrid.api.setPinnedTopRowData(pinned);

        this.theGrid.api.refreshCells({force: true});

        this.restoreSubscriptionsOfExpirationColumns();

        // Broadcast ES hedging mode so child components sync
        this._messageBus.publish({
            topic: 'Hg.EsHedgingModeChanged',
            payload: { enabled: this.esHedgingEnabled }
        });

        // Announce that the matrix re-resolved its strike context (target instrument + option chain ->
        // nearest expiration) after this portfolio change. The CoPilot reads that context FROM the matrix
        // (readTargetInstrument / resolveNearestStrikeExpiration) but is OnPush and cannot observe the async
        // set. Emitting HERE — the data owner, after tradingInstrument + hedgeMatrixDataService are built
        // above — lets the CoPilot re-read with the data guaranteed present (race-free). Fact-named after
        // what happened (the matrix's context changed), not after any consumer.
        this._messageBus.publish({ topic: 'Hg.HedgeMatrixStrikeContextChanged', payload: {} });

        // CR-20260901-005: LAST — the modifyHege(col, false) init loop above pushes entries;
        // history must start empty for the newly selected portfolio.
        this.clearMatrixUndoHistory();
    }

    fillReferenceLists() {
        const hedges = this.hedgeMatrixDataService.getHedges();

        this.hedgeList = hedges.map(x => {

            const id = x.id;
            const label = x.name;
            const side = x.type;

            const expirationsString = getHedgeExpirationsString(this, x, 'original');
            const dayOfWeeksString = getDaysOfWeekString(this, x, 'original');

            return {
                id,
                label,
                side,
                expirationsString,
                dayOfWeeksString,
                hedge: x
            };

        });

        this.expirationList = Enumerable.from(hedges)
            .selectMany(x => {
                const expirations = this.hedgeMatrixDataService.getHedgeExpirations(x.id);
                const data = expirations.map(y => {
                    return {
                        type: x.type,
                        expiration: y
                    }
                });
                return data;
            })
            .distinct((el) => `${el.type}${el.expiration}`)
            .groupBy(x => x.type)
            .selectMany(x => {

                const baseChain = this.hedgeMatrixDataService.getChainForUnderlying(this.tradingInstrument?.ticker);
                const data = x.select(y => {
                    const optExpDesc = baseChain?.expirations?.find(e => e.optionExpirationDate === y.expiration);
                    const d = {
                        side: x.key(),
                        expiration: y.expiration,
                        expirationFriendly: makeGuiFriendlyExpirationDate(y.expiration),
                        expirationFull: makeFullExpirationDate(y.expiration, this.getCursorDate()),
                        dayOfWeek: makeDayOfWeekDate(y.expiration, false, this.getCursorDate()),
                        dayOfWeekShort: makeDayOfWeekDate(y.expiration, true, this.getCursorDate()),
                        expirationTicker: optExpDesc?.expirationTicker,
                        underlyingTicker: optExpDesc?.underlyingTicker,
                    };
                    return d;
                });

                return data;

            })
            .orderBy(x => x.side)
            .thenBy(x => x.expiration)
            .toArray();

        const getHedgesByExpiration = (side: 'Call' | 'Put') => {
            const hedgesByExpiration = Enumerable.from(this.expirationList)
                .where(x => x.side === side)
                .select(exp => {

                    const hedgesWithExpiration = hedges
                        .filter(x => x.type === side)
                        .filter(h => baseHedgeIds.has(h.id))
                        .filter(h => {
                            const hedgeExpirations = this.hedgeMatrixDataService.getHedgeExpirations(h.id);
                            return hedgeExpirations.includes(exp.expiration);
                        });

                    const hedges1 = hedgesWithExpiration.map(h => {
                        const find = this.hedgeList.find(y => y.id === h.id);
                        return find;
                    })

                    return {
                        expiration: exp,
                        hedges: hedges1
                    }
                })
                .toArray();

            return hedgesByExpiration;
        }

        // Filter lists to base underlying only BEFORE building hedgesByExpiration
        const baseUnderlying = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const nonBaseUnderlyings = this.hedgeMatrixDataService.getActiveNonBaseUnderlyings();

        this.sortByFuturesExpiration(nonBaseUnderlyings);

        const baseHedgeIds = new Set(
            this.hedgeMatrixDataService.getHedgesByUnderlying(baseUnderlying).map(h => h.id)
        );
        this.hedgeList = this.hedgeList.filter(h => baseHedgeIds.has(h.id));
        this.expirationList = this.expirationList.filter(x => {
            return hedges.filter(h => baseHedgeIds.has(h.id) && h.type === x.side)
                .some(h => this.hedgeMatrixDataService.getHedgeExpirations(h.id).includes(x.expiration));
        });
        this.pnlExpirationsList = JSON.parse(JSON.stringify(this.expirationList.slice()));

        this.uniqueExpirationList = Enumerable.from(this.expirationList)
            .groupBy(x => x.expiration)
            .orderBy(x => x.key())
            .select(x => {
                const dteParts = x.first().dayOfWeek.split(' ');

                let dte;

                if (dteParts.length > 1) {
                    dte = dteParts[1];
                }

                return {
                    expiration: x.key(),
                    expirationFull: x.first().expirationFriendly,
                    dayOfWeek: x.first().dayOfWeek,
                    dayOfWeekShort: x.first().dayOfWeekShort,
                    dte: dte
                };
            })
            .toArray();

        this.callHedgesByExpiration = getHedgesByExpiration('Call');

        this.putHedgesByExpiration = getHedgesByExpiration('Put');

        // Build per-ES-contract reference lists (only when ES hedging is enabled)
        if (!this.esHedgingEnabled) {
            this.nonBaseUnderlyingSections = [];
            this.esHedgeList = [];
            this.esExpirationList = [];
            this.esPnlExpirationsList = [];
            this.esUniqueExpirationList = [];
            this.esCallHedgesByExpiration = [];
            this.esPutHedgesByExpiration = [];
            return;
        }

        this.nonBaseUnderlyingSections = nonBaseUnderlyings
            .filter(ul => this.hedgeMatrixDataService.getHedgesByUnderlying(ul).length > 0)
            .map(ul => {
            const ulHedges = this.hedgeMatrixDataService.getHedgesByUnderlying(ul);
            const ulHedgeList = ulHedges.map(x => ({
                id: x.id,
                label: x.name,
                side: x.type,
                expirationsString: getHedgeExpirationsString(this, x, 'original'),
                dayOfWeeksString: getDaysOfWeekString(this, x, 'original'),
                hedge: x
            }));

            const ulChain = this.hedgeMatrixDataService.getChainForUnderlying(ul);
            const ulExpirationList = Enumerable.from(ulHedges)
                .selectMany(x => {
                    const exps = this.hedgeMatrixDataService.getHedgeExpirations(x.id);
                    return exps.map(y => ({ type: x.type, expiration: y }));
                })
                .distinct(el => `${el.type}${el.expiration}`)
                .select(y => {
                    const optExpDesc = ulChain?.expirations?.find(e => e.optionExpirationDate === y.expiration);
                    return {
                        side: y.type,
                        expiration: y.expiration,
                        expirationFriendly: makeGuiFriendlyExpirationDate(y.expiration),
                        expirationFull: makeGuiFriendlyExpirationDateDte(y.expiration),
                        dayOfWeek: makeDayOfWeekDate(y.expiration),
                        dayOfWeekShort: makeDayOfWeekDate(y.expiration, true),
                        expirationTicker: optExpDesc?.expirationTicker,
                        underlyingTicker: optExpDesc?.underlyingTicker,
                    } as ExpirationDescriptor;
                })
                .orderBy(x => x.side)
                .thenBy(x => x.expiration)
                .toArray();

            const ulDisplayName = this._tiService.getInstrumentByTicker(ul)?.displayName || ul;
            return {
                underlying: ul,
                displayName: ulDisplayName,
                hedgeList: ulHedgeList,
                expirationList: ulExpirationList,
                pnlExpirationsList: JSON.parse(JSON.stringify(ulExpirationList)),
            };
        });

        // Build consolidated ES reference lists (all ES contracts combined)
        this.esHedgeList = this.nonBaseUnderlyingSections.flatMap(s => s.hedgeList);
        this.esExpirationList = this.nonBaseUnderlyingSections.flatMap(s => s.expirationList);
        this.esPnlExpirationsList = JSON.parse(JSON.stringify(this.esExpirationList));

        this.esUniqueExpirationList = Enumerable.from(this.esExpirationList)
            .groupBy(x => x.expiration)
            .orderBy(x => x.key())
            .select(x => {
                const dteParts = x.first().dayOfWeek.split(' ');
                let dte;
                if (dteParts.length > 1) {
                    dte = dteParts[1];
                }
                return {
                    expiration: x.key(),
                    expirationFull: x.first().expirationFriendly,
                    dayOfWeek: x.first().dayOfWeek,
                    dayOfWeekShort: x.first().dayOfWeekShort,
                    dte: dte
                };
            })
            .toArray();

        const esHedgeIds = new Set(this.esHedgeList.map(h => h.id));
        const getEsHedgesByExpiration = (side: 'Call' | 'Put') => {
            return Enumerable.from(this.esExpirationList)
                .where(x => x.side === side)
                .select(exp => {
                    const matchingHedges = hedges
                        .filter(h => esHedgeIds.has(h.id) && h.type === side)
                        .filter(h => {
                            const hExps = this.hedgeMatrixDataService.getHedgeExpirations(h.id);
                            return hExps.includes(exp.expiration);
                        })
                        .map(h => this.esHedgeList.find(y => y.id === h.id))
                        .filter(h => !!h);

                    return { expiration: exp, hedges: matchingHedges };
                })
                .toArray();
        };

        this.esCallHedgesByExpiration = getEsHedgesByExpiration('Call');
        this.esPutHedgesByExpiration = getEsHedgesByExpiration('Put');
    }

    private getPinnedRows(): HedgeMatrixRow[] {
        return [
            {isPinned: true, isExpiration: true},
            {isPinned: true, isDayOfWeek: true},
            {isPinned: true, isHedgePrice: true},
        ]
    }

    @DetectMethodChanges()
    onChange() {
        if (isVoid(this.theGrid)) {
            return;
        }
        this.theGrid.api.refreshCells({force: true});
    }

    @DetectMethodChanges()
    onOptionChainsChanged(payload?: {ticker?: string, failed?: boolean}): void {
        const data = this._hedgeMatrixDataService;
        if (!data || this._isRestoringGridState) { return; }
        if (payload?.ticker && !data.getChainForUnderlying(payload.ticker)) { return; }
        data.refreshOptionSeries(payload?.ticker);
        this.fillReferenceLists();
        this.theGrid?.api.refreshCells({force: true});
        this.theGrid?.api.refreshHeader();
        if (payload?.failed) { this._toastr.warning('Option Series Refresh Failed. Retry After Reconnecting.'); }
        this._messageBus.publish({topic: 'Hg.HedgeMatrixStrikeContextChanged', payload: {}});
    }

    private async getPortfolioUnderlying(portfolio: ApgPortfolio): Promise<string> {
        const underlying = await this._apgDataService.getUnderlyingOfPortfolio(portfolio);
        return underlying;
    }

    private async reloadData(): Promise<void> {
        // CR-20260901-005: the book is re-read from source — snapshots of the previous in-memory
        // state (hedge ids may not even survive the reload) must not be restorable across it.
        this.clearMatrixUndoHistory();
        await this._hedgeMatrixDataService.onPortfolioSelected();
        this.fillReferenceLists();
        this.applyColumnDefs(getColumnDefs(this));
        this.removeOrphanCompanionRows();
        this.recalculatePnls();
        this.theGrid.api.refreshCells({force: true});
    }

    async onCenterStrikeChanged(): Promise<void> {
        if (isVoid(this.centerStrike) && this.tradingInstrument) {
            this.centerStrike = this._atmStrikeService.getCurrentAtm(this.tradingInstrument.ticker);
        }
        this.rebuildRows();
        await this._userSettingsService.setValue(this.perPortfolioKey(CenterStrikeKey), this.centerStrike);
    }

    async onStrikeStepChanged(): Promise<void> {
        if (isVoid(this.strikeStep) && this.strikeSteps?.length > 0) {
            this.strikeStep = this.strikeSteps[0];
        }
        this.rebuildRows();
        await this._userSettingsService.setValue(this.perPortfolioKey(StrikeStepKey), this.strikeStep);
    }

    async onNumberOfStrikesChanged() {
        this.rebuildRows();
        await this._userSettingsService.setValue(this.perPortfolioKey(StrikesNoKey), this.numOfStrikes);
    }

    async onShowPortfolioPositionsChanged() {
        if (isVoid(this.theGrid)) {
            return;
        }

        this.theGrid.api.redrawRows();

        await this._userSettingsService.setValue(ShowPositionsKey, this.showPortfolioPositions);
    }

    @DetectMethodChanges()
    async onEsHedgingModeChanged($event: DxValueChanged<boolean>) {
        if (!$event.event) {
            return;
        }

        // ES hedging is only available for SPX-family underlyings (CR-20260606-001).
        if (!this.isEsEligible) {
            this.esHedgingEnabled = false;
            return;
        }

        const newValue = $event.value;

        // Safety guard: cannot disable if ES hedges exist
        if (!newValue && this.hedgeMatrixDataService) {
            const baseUnderlying = this.hedgeMatrixDataService.getPortfolioUnderlying();
            const hasEsHedges = this.hedgeMatrixDataService.getHedges()
                .some(h => (h.underlying || baseUnderlying) !== baseUnderlying);
            if (hasEsHedges) {
                this._toastr.error('Remove all ES hedges before disabling ES Hedging mode');
                // Force-reset the DX switch widget back to ON
                setTimeout(() => $event.component.option('value', true));
                return;
            }
        }

        this.esHedgingEnabled = newValue;

        // Save per-portfolio
        if (this.selectedPortfolio) {
            const pfKey = makePortfolioKey(this.selectedPortfolio);
            await this._userSettingsService.setValue(`${EsHedgingModeKey}.${pfKey}`, this.esHedgingEnabled);
        }

        // Broadcast state change
        this._messageBus.publish({
            topic: 'Hg.EsHedgingModeChanged',
            payload: { enabled: this.esHedgingEnabled }
        });

        // Rebuild grid
        if (this.theGrid && this.hedgeMatrixDataService) {
            this.fillReferenceLists();
            const columnDefs = getColumnDefs(this);
            this.applyColumnDefs(columnDefs);
            this.theGrid.api.refreshCells({force: true});
        }
    }

    getHedgePriceFormatted(hedge: HedgeData) {
        const px = this.hedgesPricingService.getHedgeGroupCost(hedge.id);
        if (!isValidNumber(px)) {
            return null;
        }
        const price = defaultCurrencyFormatter(px);
        return price;
    }

    getHedgePriceWithTrans(hedge: HedgeData): number | undefined {
        const id = hedge.id;

        const hedgeCost = this._hedgesPricingService.getHedgeGroupCost(id) || 0;

        const transCost = this.hedgeMatrixDataService
            .getTransCostAsOwned(hedge.id) || 0;

        const sum = hedgeCost + transCost;

        return sum;
    }

    private restoreValues() {
        // # of strikes, strike step and ATM (center) strike are per-portfolio
        // (CR-20260606-001) and restored on portfolio select; only the global
        // show-positions flag is restored here at init.
        const showPositions = this._userSettingsService.getValue<boolean>(ShowPositionsKey);
        this.showPortfolioPositions = showPositions || false;
    }

    private perPortfolioKey(baseKey: string): string {
        return `${baseKey}.${makePortfolioKey(this.selectedPortfolio)}`;
    }

    private restorePerPortfolioMatrixSettings() {
        if (!this.selectedPortfolio) {
            return;
        }

        const centerStrike = this._userSettingsService.getValue<number>(this.perPortfolioKey(CenterStrikeKey));
        this.centerStrike = centerStrike;

        const strikeStep = this._userSettingsService.getValue<number>(this.perPortfolioKey(StrikeStepKey));
        this.strikeStep = strikeStep || 5;

        const strikesNo = this._userSettingsService.getValue<number>(this.perPortfolioKey(StrikesNoKey));
        this.numOfStrikes = strikesNo || 20;
    }

    // ES hedging applies only to SPX-family underlyings (CR-20260606-001).
    get isEsEligible(): boolean {
        const ul = this.hedgeMatrixDataService?.getPortfolioUnderlying() || this.selectedPortfolio?.asset;
        return isEsHedgeableUnderlying(ul);
    }

    private restoreEsHedgingMode() {
        if (!this.selectedPortfolio || !this.isEsEligible) {
            this.esHedgingEnabled = false;
            return;
        }
        const pfKey = makePortfolioKey(this.selectedPortfolio);
        const saved = this._userSettingsService.getValue<boolean>(`${EsHedgingModeKey}.${pfKey}`);
        this.esHedgingEnabled = saved || false;
    }

    @DetectMethodChanges()
    toggleControlsPanel() {
        this.controlPanelExpanded = !this.controlPanelExpanded;
    }


    //
    // Hedge Selected Dialog
    //
    hedgeList: { id: string, label: string, side: 'Call' | 'Put' }[] = [];

    expirationList: ExpirationDescriptor[] = [];

    pnlExpirationsList: ExpirationDescriptor[] = [];

    uniqueExpirationList: Partial<ExpirationDescriptor>[] = [];

    callHedgesByExpiration: any[] = [];

    putHedgesByExpiration: any[] = [];

    /** Per-underlying section data for non-base underlyings (e.g., ES contracts) */
    nonBaseUnderlyingSections: {
        underlying: string;
        displayName: string;
        hedgeList: { id: string, label: string, side: 'Call' | 'Put' }[];
        expirationList: ExpirationDescriptor[];
        pnlExpirationsList: ExpirationDescriptor[];
    }[] = [];

    /** Consolidated ES reference lists — mirrors SPX lists but for all ES contracts combined */
    esHedgeList: any[] = [];
    esExpirationList: ExpirationDescriptor[] = [];
    esPnlExpirationsList: ExpirationDescriptor[] = [];
    esUniqueExpirationList: Partial<ExpirationDescriptor>[] = [];
    esCallHedgesByExpiration: any[] = [];
    esPutHedgesByExpiration: any[] = [];

    get selectedCallHedges(): number {
        return this.getSelectedHedgesCount('Call');
    }

    get selectedPutHedges(): number {
        return this.getSelectedHedgesCount('Put');
    };

    get selectedCallExpirations(): number {
        return this.getSelectedExpirationsCount('Call');
    };

    get selectedPutExpirations(): number {
        return this.getSelectedExpirationsCount('Put');
    };

    private getSelectedExpirationsCount(side: 'Call' | 'Put'): number {
        if (isVoid(this.theGrid)) {
            return undefined;
        }

        const baseSilo = this.hedgeMatrixDataService?.getPortfolioUnderlying();

        const amount = this.expirationList.filter(x => x.side === side)
            .map(x => {
                const colId = buildPricingColId(baseSilo, x.expiration, side);
                const column = this.theGrid.columnApi.getColumn(colId);
                if (isVoid(column)) {
                    return 0;
                }
                return column.isVisible() ? 1 : 0;
            })
            .reduce((p, c) => p + c, 0);

        return amount;
    }

    private getSelectedExpirationColumns(side: 'Call' | 'Put'): string[] {
        if (isVoid(this.theGrid)) {
            return undefined;
        }

        const baseSilo = this.hedgeMatrixDataService?.getPortfolioUnderlying();

        const amount = this.expirationList.filter(x => x.side === side)
            .map(x => {
                const colId = buildPricingColId(baseSilo, x.expiration, side);
                const column = this.theGrid.columnApi.getColumn(colId);
                if (isVoid(column)) {
                    return null;
                }
                return column.isVisible() ? x.expiration : null;
            }).filter(x => !isVoid(x));

        return amount;
    }

    private getSelectedHedgesCount(side: 'Call' | 'Put'): number {
        return this.getSelectedHedges(side).length;
    }

    getSelectedHedges(side: 'Call' | 'Put'): Column[] {
        if (isVoid(this.theGrid)) {
            return [];
        }

        const baseSilo = this.hedgeMatrixDataService?.getPortfolioUnderlying();

        const columns = this.hedgeList
            .filter(x => x.side === side)
            .map(x => {
                const hedge = this.hedgeMatrixDataService.getHedge(x.id);
                const silo = hedge?.underlying || baseSilo;
                const colId = buildHedgeColId(silo, x.id, 'qty');
                const column = this.theGrid.columnApi.getColumn(colId);
                if (isVoid(column) || !column.isVisible()) {
                    return undefined;
                }
                return column;
            })
            .filter(x => !isVoid(x));

        return columns;
    }

    getSelectedHedgesIds(side: 'Call' | 'Put'): string[] {
        if (isVoid(this.theGrid)) {
            return [];
        }

        const baseSilo = this.hedgeMatrixDataService?.getPortfolioUnderlying();

        const columns = this.hedgeList
            .filter(x => x.side === side)
            .map(x => {
                const hedge = this.hedgeMatrixDataService.getHedge(x.id);
                const silo = hedge?.underlying || baseSilo;
                const colId = buildHedgeColId(silo, x.id, 'qty');
                const column = this.theGrid.columnApi.getColumn(colId);
                if (isVoid(column) || !column.isVisible()) {
                    return undefined;
                }
                return x.id;
            })
            .filter(x => !isVoid(x));

        return columns;
    }


    @DetectMethodChanges()
    onHedgeSelectionChanged(hedge: any, value?: boolean) {

        const id = hedge.id;
        const hedgeData_ = this.hedgeMatrixDataService.getHedge(id);
        const silo = hedgeData_?.underlying || this.hedgeMatrixDataService.getPortfolioUnderlying();

        const column = this.theGrid.columnApi.getColumn(buildHedgeColId(silo, id, 'qty'));

        const transColumn = this.theGrid.columnApi.getColumn(buildHedgeColId(silo, id, 'trans'));

        const outcomeColumn = this.theGrid.columnApi.getColumn(buildHedgeColId(silo, id, 'outcome'));

        if (isNullOrUndefined(value)) {
            value = !column.isVisible();
        }

        if (!isVoid(column)) {
            this.theGrid.columnApi.setColumnVisible(column, value);
        }

        let hasModifications = false;

        if (value) {

            const hedgeData = this.hedgeMatrixDataService.getHedge(
                hedge.id
            );


            hasModifications = hedgeData?.underModification;

            value = hasModifications;
        }

        if (!isVoid(transColumn)) {
            this.theGrid.columnApi.setColumnVisible(transColumn, value);

        }

        if (!isVoid(outcomeColumn)) {
            this.theGrid.columnApi.setColumnVisible(outcomeColumn, value);
        }

        this.theGrid.api.refreshCells({force: true});
    }


    @DetectMethodChanges()
    onExpirationSelectionChanged(expiration: { expiration: string, side: 'Call' | 'Put' }, value?: boolean, silo?: string) {

        const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const resolvedSilo = silo || baseSilo;
        const id = buildPricingColId(resolvedSilo, expiration.expiration, expiration.side);

        const column = this.theGrid.columnApi.getColumn(id);

        if (isVoid(column)) {
            return;
        }

        if (isNullOrUndefined(value)) {
            value = !column.isVisible();
        }

        this.theGrid.columnApi.setColumnVisible(column, value);

        this.subscribePricingColumn(expiration.expiration, expiration.side, resolvedSilo, value);

        this.theGrid.api.refreshCells({force: true});
    }

    subscribePricingColumn(expiration: string, side: 'Call' | 'Put', silo?: string, subscribe: boolean = true) {
        const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const resolvedSilo = silo || baseSilo;
        const isNonBase = resolvedSilo !== baseSilo;
        const fairValue = isNonBase
            ? this.hedgeMatrixDataService.getFairValueOffset(resolvedSilo)
            : 0;
        const underlying = isNonBase ? resolvedSilo : undefined;

        this.theGrid.api.forEachNode(node => {
            const strike: number = node.data.strike;
            if (!isValidNumber(strike, true)) {
                return;
            }

            const ticker = this.hedgeMatrixDataService.getTickerForCell(
                strike + fairValue,
                expiration,
                side,
                underlying
            );

            if (!ticker) {
                return;
            }

            if (!subscribe) {
                this.lastQuoteCache.unsubscribeTicker(ticker);
            } else {
                this.lastQuoteCache.subscribeTicker(ticker);
            }
        });
    }

    toggleSelectAllHedges(args: DxValueChanged<boolean>, side?: 'Call' | 'Put') {
        if (!args.event) {
            return;
        }

        this.hedgeList
            .filter(x => x => isVoid(side) ? true : x.side === side)
            .forEach(x => this.onHedgeSelectionChanged(x, args.value));
    }

    toggleSelectAllExpirations(args: DxValueChanged<boolean>, side?: 'Call' | 'Put') {
        if (!args.event) {
            return;
        }

        this.expirationList
            .filter(x => x => isVoid(side) ? true : x.side === side)
            .forEach(x => this.onExpirationSelectionChanged(x, args.value));
    }

    getColumnSelectorTextHedges() {

        const selectedHedges = this.selectedCallHedges + this.selectedPutHedges;
        const totalHedges = this.hedgeList.length;

        if (totalHedges === selectedHedges) {
            return "All Hedges Selected";
        }

        return `${selectedHedges} of ${totalHedges} Hedges Selected`;
    }

    getColumnSelectorTextExpirations() {

        const baseSilo = this.hedgeMatrixDataService?.getPortfolioUnderlying();

        const selectedExpirations = this.uniqueExpirationList
            .map(x => {

                const callColId = buildPricingColId(baseSilo, x.expiration, 'Call');

                const putColId = buildPricingColId(baseSilo, x.expiration, 'Put');

                const callColumn = this.theGrid.columnApi.getColumn(callColId);

                const putColumn = this.theGrid.columnApi.getColumn(putColId);

                if (isVoid(callColumn) && isVoid(putColumn)) {
                    return null;
                }

                const callVisible = !isVoid(callColumn) && callColumn.isVisible();
                const putVisible = !isVoid(putColumn) && putColumn.isVisible();

                return (callVisible || putVisible) ? x.expiration : null;

            }).filter(x => !isVoid(x));


        const distinct = selectedExpirations.filter((v, ix, arr) => arr.indexOf(v) === ix);

        if (this.uniqueExpirationList.length === distinct.length) {
            return "All Expirations Selected";
        }

        return `${distinct.length} of ${this.uniqueExpirationList.length} Expirations Selected`;
    }

    showHedgesSelector() {
        this.hedgesSelectorCmp.show(this);
    }

    isHedgeSelected(hedge: any): boolean {
        const hedgeData = this.hedgeMatrixDataService.getHedge(hedge.id);
        const silo = hedgeData?.underlying || this.hedgeMatrixDataService.getPortfolioUnderlying();
        const colId = buildHedgeColId(silo, hedge.id, 'qty');
        const col = this.theGrid.columnApi.getColumn(colId);
        return !isVoid(col) && col.isVisible();
    }

    isExpirationSelected(expiration: any): boolean {
        const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const id = buildPricingColId(baseSilo, expiration.expiration, expiration.side);
        const col = this.theGrid.columnApi.getColumn(id);
        return !isVoid(col) && col.isVisible();
    }

    // ── ES-specific column toggle/query methods (prefixed column IDs) ──

    @DetectMethodChanges()
    onEsExpirationSelectionChanged(expiration: any, ul: string, value?: boolean) {
        const id = buildPricingColId(ul, expiration.expiration, expiration.side);
        const column = this.theGrid.columnApi.getColumn(id);
        if (!isVoid(column)) {
            const newVisible = isNullOrUndefined(value) ? !column.isVisible() : value;
            this.theGrid.columnApi.setColumnVisible(column, newVisible);

            const fairValue = this.hedgeMatrixDataService.getFairValueOffset(ul);

            this.theGrid.api.forEachNode(node => {
                const strike: number = node.data.strike;
                if (!isValidNumber(strike, true)) {
                    return;
                }
                const esStrike = strike + fairValue;
                const ticker = this.hedgeMatrixDataService.getTickerForCell(
                    esStrike, expiration.expiration, expiration.side, ul
                );
                if (!ticker) { return; }
                if (!newVisible) {
                    this.lastQuoteCache.unsubscribeTicker(ticker);
                } else {
                    this.lastQuoteCache.subscribeTicker(ticker);
                }
            });

            this.theGrid.api.refreshCells({ force: true });
        }
    }

    isEsExpirationSelected(expiration: any, ul: string): boolean {
        const id = buildPricingColId(ul, expiration.expiration, expiration.side);
        const col = this.theGrid.columnApi.getColumn(id);
        return !isVoid(col) && col.isVisible();
    }

    // Mirrors onPnlExpirationSelectionChanged (SPX): recalc on show + explicit refresh so the
    // column and the dialog checkbox update in the same pass, not on the next incidental one.
    @DetectMethodChanges()
    onEsPnlExpirationSelectionChanged(expiration: any, value: boolean, allInclusive: boolean, ul: string) {
        const id = buildPnlExpirationColId(ul, expiration.expiration, expiration.side, allInclusive);
        const column = this.theGrid.columnApi.getColumn(id);
        if (!isVoid(column)) {
            const visible = isVoid(value) ? !column.isVisible() : value;
            this.theGrid.columnApi.setColumnVisible(column, visible);
            if (visible) {
                this.recalculatePnls();
            }
            this.theGrid.api.refreshCells({force: true});
        }
    }

    isEsPnlExpirationSelected(expiration: any, allInclusive: boolean, ul: string): boolean {
        const id = buildPnlExpirationColId(ul, expiration.expiration, expiration.side, allInclusive);
        const col = this.theGrid.columnApi.getColumn(id);
        return !isVoid(col) && col.isVisible();
    }

    @DetectMethodChanges()
    onEsGrandTotalExpirationSelectionChanged(expiration: any, ul: string) {
        const id = buildPnlTotalColId(ul, expiration.expiration);
        const column = this.theGrid.columnApi.getColumn(id);
        if (!isVoid(column)) {
            const visible = !column.isVisible();
            this.theGrid.columnApi.setColumnVisible(column, visible);
            if (visible) {
                this.recalculatePnls();
            }
            this.theGrid.api.refreshCells({force: true});
        }
    }

    isEsGrandTotalExpirationSelected(expiration: any, ul: string): boolean {
        const id = buildPnlTotalColId(ul, expiration.expiration);
        const col = this.theGrid.columnApi.getColumn(id);
        return !isVoid(col) && col.isVisible();
    }

    modifyHege(column: Column, visibility?: boolean) {

        this.theGrid.api.showLoadingOverlay();

        try {
            const hedgeId = getHedgeIdFromColId(column.getColId());

            const hedgeData = this.hedgeMatrixDataService.getHedge(
                hedgeId
            );

            if (isVoid(hedgeData)) {
                this._toastr.error('Cannot Determine Hedge Data');
                return;
            }

            // CR-20260901-005: entering modification seeds outcome=qty; exiting WIPES trans/outcome.
            // Either direction is a quantity-mutating gesture — one transaction (END sits at the tail
            // of the mutation timeout below). The init-time calls from onPortfolioSelected land in
            // history too, but that method clears it right after.
            this.ensureMatrixUndoTransaction();

            const hedgeSilo = hedgeData.underlying || this.hedgeMatrixDataService.getPortfolioUnderlying();
            let colId = buildHedgeColId(hedgeSilo, hedgeData.id, 'trans');
            let col = this.theGrid.columnApi.getColumn(colId);
            console.assert(!isVoid(col));

            let colVisibility = visibility;
            if (isNullOrUndefined(colVisibility)) {
                colVisibility = !col.isVisible()
            }

            this.theGrid.columnApi.setColumnVisible(col, colVisibility);

            colId = buildHedgeColId(hedgeSilo, hedgeData.id, 'outcome');

            col = this.theGrid.columnApi.getColumn(colId);

            colVisibility = visibility;

            if (isNullOrUndefined(colVisibility)) {
                colVisibility = !col.isVisible()
            }

            this.theGrid.columnApi.setColumnVisible(col, colVisibility);

            const self = this;

            const f = !col.isVisible()
                ? () => {
                    if (self.slideHedgeDialog?.visible) {
                        self.slideHedgeDialog.onHidden();
                    }
                    return self.hedgeMatrixDataService.onHedgeModificationFinished(hedgeData);
                }
                : () => self.hedgeMatrixDataService.onHedgeModificationStarted(hedgeData);

            setTimeout(() => {
                const needToUpdateRows = f();
                if (needToUpdateRows) {
                    // Row spans may have changed — surgical row update
                    const allRows: HedgeMatrixRow[] = [];
                    self.theGrid.api.forEachNode(n => allRows.push(n.data));

                    // Recalculate which strikes need row span
                    const hedges = self.hedgeMatrixDataService.getHedges();
                    const doubleStrikes = new Set<number>();
                    hedges.forEach(h => {
                        const cellData = self.hedgeMatrixDataService.getCellDataForHedge(h.id);
                        const groups = {};
                        (cellData || []).forEach(cd => {
                            groups[cd.strike] = (groups[cd.strike] || 0) + 1;
                        });
                        Object.keys(groups).forEach(s => {
                            if (groups[s] > 1) doubleStrikes.add(parseInt(s, 10));
                        });
                    });

                    // Remove orphan companion rows and fix row spans
                    const toAdd: HedgeMatrixRow[] = [];
                    const toRemove: HedgeMatrixRow[] = [];

                    allRows.forEach(row => {
                        if (row.isPinned || row.strike == null) return;
                        const isCompanion = row.rowId && row.rowId.includes('-');
                        const shouldSpan = doubleStrikes.has(row.strike);

                        if (isCompanion && !shouldSpan) {
                            toRemove.push(row);
                        } else if (!isCompanion) {
                            if (shouldSpan && row.rowSpan !== 2) {
                                row.rowSpan = 2;
                                row.isMultiExpiration = true;
                                const companionId = row.strike + '-1';
                                if (!allRows.some(r => r.rowId === companionId)) {
                                    toAdd.push({
                                        rowId: companionId,
                                        strike: row.strike,
                                        rowSpan: 1,
                                        isMultiExpiration: true
                                    });
                                }
                            } else if (!shouldSpan && row.rowSpan === 2) {
                                row.rowSpan = 1;
                                row.isMultiExpiration = false;
                            }
                        }
                    });

                    if (toAdd.length > 0 || toRemove.length > 0) {
                        self.theGrid.api.applyTransaction({ add: toAdd, remove: toRemove });
                        const baseSilo = self.hedgeMatrixDataService.getPortfolioUnderlying();
                        self.theGrid.api.setSortModel([
                            { colId: buildStrikeColId(baseSilo), sort: 'desc' }
                        ]);
                        self.theGrid.api.redrawRows();
                    }
                }
                this.recalculatePnls();
                self.theGrid.api.refreshCells({force: true});

                // CR-20260901-005: END — f() (modification start/finish) has run; its cell writes
                // bypass setQty, which is exactly why the transaction is diffed, not logged.
                this.endMatrixUndoTransaction();
            });

        } finally {
            setTimeout(() => {
                this.theGrid.api.hideOverlay()
            }, 500);
        }
    }

    // CoPilot (CR-20260619-001) divergence state (relocated from the Hedge Pad): which hedges are under
    // CoPilot close-Preview, the warn-once-per-Conversation latch, and which previewed hedges the user has
    // since manually touched (so un-Preview leaves their custom edit intact — M2).
    private _copilotPreviewedHedgeIds = new Set<string>();
    private _copilotMatrixDivergenceArmed = true;
    private _copilotDivergedHedgeIds = new Set<string>();

    // CR-20260724-001 — hedges this Matrix CREATED for a previewed Solution, so un-Preview deletes
    // exactly those and nothing else. A list per Solution, not a single id: one composition can be
    // two-sided (calls capping a peak, puts filling a shortfall) and a Matrix hedge is single-sided.
    private _copilotOpenHedgesBySolution: { [solutionId: string]: string[] } = {};

    // CR-20260724-001 stage 7 (Attribution) — EXISTING hedges this Solution put into modification,
    // as opposed to hedges it created. Tracked separately because un-Preview undoes them differently:
    // a created hedge is deleted, a modified one is returned to its held state (the same exit the
    // close-Preview uses). Deleting a hedge the trader already owned would destroy their position.
    private _copilotModifiedHedgesBySolution: { [solutionId: string]: string[] } = {};
    private _availableAdjustmentByHedge: { [hedgeId: string]: import('../copilot/available-adjustment-operations').AdjustmentAcquisitionReference } = {};

    bindAvailableAdjustment(solutionId: string,
        reference: import('../copilot/available-adjustment-operations').AdjustmentAcquisitionReference): void {
        (this._copilotModifiedHedgesBySolution[solutionId] || []).forEach(id => {
            this._availableAdjustmentByHedge[id] = {...reference};
        });
    }

    hasAdjustmentPreview(solutionId: string): boolean {
        const ids = this._copilotModifiedHedgesBySolution[solutionId] || [];
        return ids.length > 0 && ids.every(id => this.hedgeMatrixDataService.getHedge(id)?.underModification === true);
    }

    forgetAdjustmentPreview(solutionId: string): void {
        // Bookkeeping only: the matrix already removed the preview. Never revert saved holdings.
        if (this.hasAdjustmentPreview(solutionId)) { return; }
        (this._copilotModifiedHedgesBySolution[solutionId] || []).forEach(id => {
            delete this._availableAdjustmentByHedge[id];
            this._copilotPreviewedHedgeIds.delete(id); this._copilotDivergedHedgeIds.delete(id);
        });
        delete this._copilotModifiedHedgesBySolution[solutionId];
    }

    // Called ONLY from the grid value-setter (a genuine USER cell commit). CoPilot's own preview writes go
    // through the data service directly and never reach the setter, so this is a clean user-only signal.
    // First manual edit on a CoPilot-previewed hedge -> warn ONCE per Conversation + mark it diverged.
    noteUserMatrixEdit(hedgeId: string): void {
        delete this._availableAdjustmentByHedge[hedgeId];
        if (!this._copilotPreviewedHedgeIds.has(hedgeId)) {
            return;   // edit on a non-CoPilot hedge — not divergence
        }
        this._copilotDivergedHedgeIds.add(hedgeId);   // M2: un-Preview must not wipe this manual edit
        if (!this._copilotMatrixDivergenceArmed) {
            return;   // already warned this Conversation (NFR-23 once)
        }
        this._copilotMatrixDivergenceArmed = false;
        this._toastr.warning(
            'You are making manual changes on top of the ORION-suggested hedges. You are now leaving the ORION-suggested state — your changes are custom.',
            'Custom changes',
            { closeButton: true, disableTimeOut: true }
        );
    }

    // Re-arm the warn-once latch on a true Conversation boundary (new-conversation / portfolio-switch).
    // The facade's resetCopilotDivergence drives this; in-flight previews tear down via unPreviewCopilotClose.
    resetCopilotMatrixDivergence(): void {
        this._availableAdjustmentByHedge = {};
        this._copilotMatrixDivergenceArmed = true;
        // Conversation boundary: previews are torn down before this runs, so the Sets should be empty.
        // Clear defensively to drop any residue left by a torn-down-grid un-Preview.
        this._copilotPreviewedHedgeIds.clear();
        this._copilotDivergedHedgeIds.clear();
        // CR-20260901-005 round 4: manual-edit history deliberately survives a CoPilot
        // Conversation boundary — it belongs to the trader, not to ORION.
    }

    // ---- CR-20260901-005: Undo/Redo for quantity-changing gestures --------------------------------

    // ORION preview policy (owner ruling 2026-09-01, round 4): the preview's OWN writes are never
    // undo steps — they bypass the capture points by construction. Manual edits, including those
    // made WHILE a preview is up, are ordinary transactions: editing a previewed state breaks the
    // ORION link anyway, and the existing divergence mechanism (warn once + mark diverged) is the
    // guard for that seam. Undo/redo applied onto a previewed hedge is a manual change like any
    // other, so the apply routes affected hedges through the same noteUserMatrixEdit.
    clearMatrixUndoHistory(): void {
        this._matrixUndo.clear();
    }

    // Grid event: a cell edit session opened → BEGIN transaction. The before-state must be taken
    // NOW — the punch-pad editor fires amend/roll-other-legs into the data service BEFORE the value
    // setter sees the edited cell, so a later snapshot would miss those cells.
    onMatrixCellEditingStarted(): void {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return;
        }
        this._matrixUndo.beginTransaction(ds.getStateSnapshot());
    }

    // Grid event: the edit session closed. A committed transaction was already consumed by END;
    // a cancelled/no-op session drops its before-state here.
    onMatrixCellEditingStopped(): void {
        this._matrixUndo.abortTransaction();
    }

    // BEGIN for gestures reaching the mutation code without a cell-editor session (slide,
    // modification toggles, paste-into-cell). Keeps an already-open edit-session transaction —
    // its before-state is the true one — instead of overwriting it.
    ensureMatrixUndoTransaction(): void {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return;
        }
        if (this._matrixUndo.hasPendingTransaction) {
            return;
        }
        this._matrixUndo.beginTransaction(ds.getStateSnapshot());
    }

    // END: diff current-vs-BEGIN, push the touched-cells delta. A gesture that changed nothing
    // pushes nothing (and leaves the redo stack intact). Callers invoke this AFTER their mutations.
    endMatrixUndoTransaction(): void {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return;
        }
        this._matrixUndo.commitTransaction(ds.getStateSnapshot());
    }

    // Keydown on the matrix root. Bound in the template; events bubble up from the focused grid cell.
    onMatrixKeyDown($event: KeyboardEvent): void {
        if (!$event.ctrlKey && !$event.metaKey) {
            return;
        }
        const key = ($event.key || '').toLowerCase();
        const isUndo = key === 'z' && !$event.shiftKey;
        const isRedo = key === 'y' || (key === 'z' && $event.shiftKey);
        if (!isUndo && !isRedo) {
            return;
        }
        // Owner decision (CR-20260901-005 Q4): inert while a cell editor is open — the editor's own
        // input keeps its native Ctrl+Z. Same for any focused input outside the grid.
        const target = $event.target as HTMLElement;
        if (target?.closest && target.closest('input, textarea, [contenteditable="true"]')) {
            return;
        }
        if (this.theGrid?.api?.getEditingCells()?.length > 0) {
            return;
        }
        if (isUndo ? !this._matrixUndo.canUndo : !this._matrixUndo.canRedo) {
            return;
        }
        $event.preventDefault();
        $event.stopPropagation();
        if (isUndo) {
            this.undoMatrixEdit();
        } else {
            this.redoMatrixEdit();
        }
    }

    @DetectMethodChanges()
    undoMatrixEdit(): void {
        this.applyMatrixUndoRedo('undo');
    }

    @DetectMethodChanges()
    redoMatrixEdit(): void {
        this.applyMatrixUndoRedo('redo');
    }

    private applyMatrixUndoRedo(direction: 'undo' | 'redo'): void {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds) || isVoid(this.theGrid)) {
            return;
        }
        const delta = direction === 'undo'
            ? this._matrixUndo.undo()
            : this._matrixUndo.redo();
        if (isVoid(delta)) {
            return;
        }

        // Cell-scoped apply: only the gesture's own cells are written (undo = before images,
        // redo = after images); everything else in the matrix — an active ORION preview overlay
        // included — stays untouched.
        ds.applyMatrixDelta(delta, direction);

        // Undo/redo IS a manual change: applied onto a CoPilot-previewed hedge it breaks the
        // ORION link like any other edit, so it runs the same divergence path (warn once per
        // Conversation + mark diverged). No-op for non-previewed hedges.
        const touchedHedgeIds = new Set<string>();
        delta.cells.forEach(c => touchedHedgeIds.add(c.hedgeId));
        delta.hedgeFields.forEach(hf => touchedHedgeIds.add(hf.hedgeId));
        touchedHedgeIds.forEach(id => this.noteUserMatrixEdit(id));

        // Tickers of restored cells (idempotent; both images so undo→redo cycles stay covered).
        delta.cells.forEach(c => {
            [c.before, c.after].forEach(img => {
                if (img?.ticker) {
                    this.lastQuoteCache.subscribeIfNotYet(img.ticker);
                }
            });
        });

        // Row sync is delta-proportional: rows are added only where a restored cell has no row,
        // rowspan companions change only where multi-expiration membership actually flipped.
        this.addMissingStrikeRows();
        syncCompanionRows(this, this.theGrid.api);

        // Column defs are rebuilt ONLY when the restore changed the expiration universe (a rolled
        // expiration appearing/disappearing changes the per-expiration pricing/P&L column set) —
        // setColumnDefs re-renders every header and was the round-2 flicker. Otherwise only the
        // Trans/Outcome visibility is re-derived, and only when a modification flag travelled
        // in the delta.
        const columnSignature = () => JSON.stringify([this.expirationList, this.esExpirationList]);
        const sigBefore = columnSignature();
        this.fillReferenceLists();
        if (columnSignature() !== sigBefore) {
            this.applyColumnDefs(getColumnDefs(this));
        } else if (delta.hedgeFields.some(hf =>
            (hf.before?.underModification || false) !== (hf.after?.underModification || false))) {
            this.syncModificationColumns();
        }

        // The tail every normal cell edit already runs (proven flicker-free): P&L recalc +
        // cell refresh + the Hg.HedgeMatrixChanged notification for the OnPush CoPilot child.
        this.recalculatePnls();
        this.theGrid.api.refreshCells({force: true});
    }

    // Undo/redo can restore a leg at a strike whose row is no longer in the grid (the ladder was
    // re-derived between capture and restore — e.g. a strike-count change). Cells render only where
    // a row exists, so missing rows are added the same way slideHedge adds its shifted-strike rows.
    // Rows are never REMOVED here — a manual leg delete leaves its row behind too.
    private addMissingStrikeRows(): void {
        const ds = this.hedgeMatrixDataService;

        const existing = new Set<number>();
        this.theGrid.api.forEachNode(n => {
            const row = n.data as HedgeMatrixRow;
            if (isVoid(row) || row.isPinned) return;
            if (isValidNumber(row.strike, true)) {
                existing.add(row.strike);
            }
        });

        const toAdd: HedgeMatrixRow[] = [];
        ds.getHedges().forEach(h => {
            (ds.getCellDataForHedge(h.id) || []).forEach(cd => {
                if (existing.has(cd.strike)) return;
                existing.add(cd.strike);
                toAdd.push({
                    rowId: cd.strike + '',
                    otm: cd.strike - this.centerStrike,
                    strike: cd.strike,
                    rowSpan: 1
                } as HedgeMatrixRow);
            });
        });

        if (toAdd.length === 0) {
            return;
        }

        this.theGrid.api.applyTransaction({ add: toAdd });

        const baseSilo = ds.getPortfolioUnderlying();
        this.theGrid.api.setSortModel([
            { colId: buildStrikeColId(baseSilo), sort: 'desc' }
        ]);

        // Newly added rows need their visible pricing-column tickers subscribed (same as slideHedge).
        const visiblePricingCols = this.theGrid.columnApi.getAllDisplayedColumns()
            .filter(c => c.isVisible() && isPricingColumn(c.getColId()));
        visiblePricingCols.forEach(col => {
            const parsed = parseColId(col.getColId());
            toAdd.forEach(row => {
                const ticker = ds.getTickerForCell(
                    row.strike, parsed.exp, parsed.side as any, parsed.silo !== baseSilo ? parsed.silo : undefined
                );
                if (ticker) {
                    this.lastQuoteCache.subscribeIfNotYet(ticker);
                }
            });
        });
    }

    // ---- end CR-20260901-005 ----------------------------------------------------------------------

    // True when the user CURRENTLY has a manual modification on this hedge — drives the CoPilot Solution
    // card's yellow "Modified" border + badge. Reflects CURRENT state (not a remembered edit), so it
    // AUTO-CLEARS when the user reverts the hedge to its original state — matching the global refresh button.
    // It uses the SAME "touched by user" notion the reads filter on, and excludes CoPilot's OWN close-Preview
    // (which also puts the hedge under modification) so a previewed card never self-flags.
    isCopilotHedgeModified(hedgeId: string): boolean {
        if (this._copilotPreviewedHedgeIds.has(hedgeId)) {
            // CoPilot owns this hedge's preview overlay; only a USER divergence on top counts (the hedge is
            // now "custom" until un-Preview / New Conversation — same stickiness as the divergence warning).
            return this._copilotDivergedHedgeIds.has(hedgeId);
        }
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return false;
        }
        const hedge = ds.getHedge(hedgeId);
        return !isVoid(hedge) && this.isHedgeTouchedByUser(hedge);
    }

    // True when the existing hedge a CoPilot Solution targets is no longer in the book — e.g. the user
    // Acquired/closed it after previewing. Keyed on the target legs' option TICKERS (stable), NOT the hedge
    // groupId: the Acquire rebuild reassigns groupIds (HedgeData.id = position.groupId, and Acquire re-pastes
    // positions with fresh groupIds), so a captured groupId goes stale for EVERY hedge — matching by id would
    // falsely mark surviving hedges as removed. A leg is "still held" iff its ticker has a non-zero RAW qty
    // somewhere in the book (raw qty, NOT net — a Preview overlay sets transQty, not qty, so a previewed-but-
    // not-acquired hedge stays held). Gone iff NONE of the target legs are still held (a full close removes
    // them all).
    isCopilotHedgeGone(tickers: string[]): boolean {
        if (isVoid(tickers) || tickers.length === 0) {
            return false;
        }
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return false;
        }
        const held = new Set<string>();
        (ds.getHedges() || []).forEach(h => {
            (ds.getCellDataForHedge(h.id) || []).forEach(c => {
                if (c && c.ticker && (c.qty || 0) !== 0) {
                    held.add(c.ticker);
                }
            });
        });
        return tickers.every(t => !held.has(t));
    }

    // ---- CoPilot facade READS (CR-20260619-001 Increment 1c) ------------------------------------------
    // The HedgeMatrix CoPilot sources its book hedges / dates / working positions from the Matrix's OWN
    // cell index — NOT the Hedge Pad. cell.hedgeId === HedgeData.id === the close-leg groupId.
    // cell.expiration is ISO 'yyyy-MM-dd' (parity with the close-tab date filter).
    //
    // Two exclusions: (1) NEW hedges (isNew) are not yet part of the book; (2) hedges the user has started
    // MANUALLY modifying (isHedgeHiddenFromCopilot) are mid-edit and must NOT surface as available/clean
    // CoPilot targets — offering CoPilot work on top of an in-flight user edit conflicts with the user's
    // intent. This is the Matrix-side parity of the settings-section getTouchedOwnedGroupIds filter.
    //
    // CR-20260901-002: hedges under COPILOT'S OWN Preview are NOT hidden. Preview sets the same
    // underModification/transQty signals a manual edit does, and hiding them here made the questionnaire's
    // book shrink (expirations vanishing to "—"), which the CR-20260831-004 changed-vs-run diff then read
    // as a trader change — a false yellow on the Expiration chip, the collapsed summary, and the FAB.
    // While a preview is clean (not user-diverged), every read below projects the hedge from its ORIGINAL
    // legs (cell.qty only — the preview overlay lives in transQty), so ORION keeps seeing exactly the book
    // its run saw. Such hedges are display-visible only: previewCopilotClose still rejects them as targets,
    // and the Close tab renders them disabled (CoPilotBookHedge.previewed).

    // A hedge the user has started modifying: it is in modification mode (underModification) and/or carries a
    // pending transaction on any cell (transQty). underModification is the primary signal (set by
    // onHedgeModificationStarted, cleared by onHedgeModificationFinished); the transQty check is the
    // belt-and-suspenders second signal, mirroring the data service's hasAnyTransactions predicate.
    private isHedgeTouchedByUser(h: HedgeData): boolean {
        if (isVoid(h)) {
            return false;
        }
        if (h.underModification === true) {
            return true;
        }
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return false;
        }
        return (ds.getCellDataForHedge(h.id) || []).some(c => isValidNumber(c.transQty, true));
    }

    // CR-20260901-002: CoPilot's own Preview owns this hedge and the user has NOT edited on top of it.
    // Its modification signals are the preview overlay, not a trader edit — the reads keep it visible.
    private isHedgePreviewOwnedClean(h: HedgeData): boolean {
        return !isVoid(h)
            && this._copilotPreviewedHedgeIds.has(h.id)
            && !this._copilotDivergedHedgeIds.has(h.id);
    }

    // The single exclusion rule for every CoPilot facade read below: new hedges and MANUALLY touched
    // hedges are hidden; a hedge under a clean CoPilot Preview is not (CR-20260901-002).
    private isHedgeHiddenFromCopilot(h: HedgeData): boolean {
        return h.isNew || (!this.isHedgePreviewOwnedClean(h) && this.isHedgeTouchedByUser(h));
    }

    // The held quantity of a cell as CoPilot must see it: for a hedge under a clean Preview, the ORIGINAL
    // book quantity (the overlay lives in transQty); otherwise the net working quantity.
    private copilotHeldQty(c: { qty?: number, transQty?: number }, previewOwned: boolean): number {
        return previewOwned ? (c.qty || 0) : (c.qty || 0) + (c.transQty || 0);
    }

    copilotReadBookHedges(): CoPilotBookHedge[] {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return [];
        }
        return ds.getHedges()
            .filter(h => !this.isHedgeHiddenFromCopilot(h))
            .map(h => ({
                id: h.id,
                label: this.makeCopilotHedgeLabel(h),
                optionType: (h.type === 'Call' || h.type === 'Put') ? h.type : null,   // CALLS/PUTS grouping
                expirations: ds.getHedgeExpirations(h.id) || [],                         // for the Close-tab date filter
                previewed: this.isHedgePreviewOwnedClean(h),                             // display-only in the Close tab
                underlying: h.underlying || ds.getPortfolioUnderlying() || null          // CR-20260924-002: the ORION ES toggle filters on it
            }));
    }

    copilotReadBookDates(): Date[] {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return [];
        }
        const seen: { [k: string]: boolean } = {};
        const out: Date[] = [];
        ds.getHedges().filter(h => !this.isHedgeHiddenFromCopilot(h)).forEach(h => {
            (ds.getHedgeExpirations(h.id) || []).forEach(exp => {
                if (isVoid(exp) || seen[exp]) {
                    return;
                }
                seen[exp] = true;
                // ISO 'yyyy-MM-dd' -> LOCAL-midnight Date so it round-trips through the close tab's
                // toCalendarDateString (local components). NEVER new Date(iso) (UTC -> off-by-one day).
                const dt = DateTime.fromFormat(exp, 'yyyy-MM-dd');
                if (dt.isValid) {
                    out.push(dt.toJSDate());
                }
            });
        });
        return out;
    }

    // Expirations offered on the ADJUST tab's selector (CR-20260814-001).
    //
    // ⚠ DELIBERATELY NOT copilotReadBookDates(). That one derives its dates from
    // getHedgeExpirations(), which reads EVERY cell of a hedge with no quantity filter and, for a
    // hedge with no cell data at all, SYNTHESISES one from getHedgeDefaultExpiration(). Either path
    // can name an expiration that contributes ZERO held legs. On Close that is harmless. On Adjust it
    // is not: the chip decides which positions are sent, so a chip with no held legs would send an
    // empty payload and the run would come back refused with nothing the trader could act on.
    //
    // So this walks the SAME hedges and the SAME held-cell test copilotReadWorkingStatePositions()
    // uses below. Every chip it returns is therefore backed by at least one position that will
    // actually be on the wire. Keep the two in step: if the projection below changes, change this too.
    copilotReadAdjustDates(): Date[] {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return [];
        }
        const seen: { [k: string]: boolean } = {};
        const out: Date[] = [];
        ds.getHedges().filter(h => !this.isHedgeHiddenFromCopilot(h)).forEach(h => {
            const previewOwned = this.isHedgePreviewOwnedClean(h);
            (ds.getCellDataForHedge(h.id) || []).forEach(c => {
                const heldQty = this.copilotHeldQty(c, previewOwned);
                if (heldQty === 0 || isVoid(c.ticker) || isVoid(c.expiration) || seen[c.expiration]) {
                    return;
                }
                seen[c.expiration] = true;
                // ISO 'yyyy-MM-dd' -> LOCAL-midnight Date, so it round-trips through
                // toCalendarDateString (local components). NEVER new Date(iso) (UTC -> off-by-one day).
                const dt = DateTime.fromFormat(c.expiration, 'yyyy-MM-dd');
                if (dt.isValid) {
                    out.push(dt.toJSDate());
                }
            });
        });
        out.sort((a, b) => a.getTime() - b.getTime());   // chronological — nearest expiration first
        return out;
    }

    copilotReadWorkingStatePositions(): CoPilotPositionDto[] {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return [];
        }
        const out: CoPilotPositionDto[] = [];
        ds.getHedges().filter(h => !this.isHedgeHiddenFromCopilot(h)).forEach(h => {
            const previewOwned = this.isHedgePreviewOwnedClean(h);
            (ds.getCellDataForHedge(h.id) || []).forEach(c => {
                const heldQty = this.copilotHeldQty(c, previewOwned);   // net working qty (parity with the Pad's asDto); ORIGINAL qty under a clean Preview
                if (heldQty === 0 || isVoid(c.ticker)) {
                    return;   // skip removed / zero-net / unplaceable cells
                }
                out.push({
                    ticker: c.ticker,                                 // full option ticker (close placement key)
                    underlying: c.underlying,
                    asset: c.underlying,
                    quantity: heldQty,                                // SIGNED (long > 0, short < 0)
                    strike: isValidNumber(c.actualStrike) ? c.actualStrike : (isValidNumber(c.strike) ? c.strike : null),
                    expiry: isVoid(c.expiration) ? null : c.expiration,   // ISO 'yyyy-MM-dd'
                    optionType: isVoid(c.optionType) ? null : c.optionType,
                    avgPrice: null,
                    label: c.label || '',
                    groupId: c.hedgeId || '',                         // === HedgeData.id (close-leg groupId)
                    color: c.color || null                            // hedge colour (FE-only; colours the Transactions view)
                });
            });
        });
        return out;
    }

    // Display label for a book hedge — never a raw ticker (memory: ui-display-names-not-raw-tickers).
    // Hedge name (its leg label), else the resolved underlying display name, else the underlying ticker.
    private makeCopilotHedgeLabel(h: HedgeData): string {
        if (!isVoid(h.name) && h.name !== '') {
            return h.name;
        }
        const ul = h.underlying || this.hedgeMatrixDataService.getPortfolioUnderlying();
        return (this._tiService.getInstrumentByTicker(ul)?.displayName) || ul || h.id;
    }

    // CoPilot (CR-20260619-001) — Preview a CLOSE of one hedge: reproduce the EXACT state a manual
    // "Modify Hedge -> Close Position" produces — enter modification mode (underModification + outcomeQty
    // seeded to held qty), reveal the hedge's Trans/Outcome columns, and write a closing transaction
    // (transQty = -legQty) on every held leg. Reversible via unPreviewCopilotClose. Returns hedge-locked
    // if the hedge is already under modification (manual or another previewed Solution) so we never clobber
    // an in-progress edit.
    previewCopilotClose(hedgeId: string): ApplyResult {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds) || isVoid(this.theGrid)) {
            return { applied: false, reason: 'missing-target-leg' };   // grid not ready / torn down
        }
        const hedge = ds.getHedge(hedgeId);
        if (isVoid(hedge)) {
            return { applied: false, reason: 'missing-target-leg' };
        }
        if (hedge.underModification === true) {
            // Already under a MANUAL modification (or a preserved divergence). CoPilot must not overwrite a
            // user modification — refuse with a distinct reason (NOT OQ-13 'hedge-locked', which means a
            // different CoPilot Solution). The user exits modification mode / starts a New Conversation to
            // let CoPilot close it again.
            return { applied: false, reason: 'hedge-modified' };
        }

        // CR-20260901-005 round 4: NO history clear here — manual-edit transactions survive
        // preview transitions (owner ruling); the preview's own writes below are simply unrecorded.
        const silo = hedge.underlying || ds.getPortfolioUnderlying();
        this.setCopilotHedgeModColumns(silo, hedge.id, true);

        ds.onHedgeModificationStarted(hedge);   // seeds outcomeQty = qty, underModification = true
        const cells = ds.getCellDataForHedge(hedgeId) || [];
        cells.forEach(cd => {
            if (typeof cd.qty === 'number' && !isNaN(cd.qty) && cd.qty !== 0) {
                // Clear the outcome (NaN) — setQtyInternal then writes transQty = -legQty: the closing
                // transaction, exactly like the manual punch-pad "Close Position".
                ds.setOutcomeQty(hedge, cd.strike, cd.expiration, Number.NaN);
            }
        });

        this._copilotPreviewedHedgeIds.add(hedge.id);   // now CoPilot-owned (divergence + un-Preview tracking)
        this.recalculatePnls();
        this.theGrid.api.refreshCells({ force: true });
        return { applied: true };
    }

    // CoPilot — Preview a set of NEW legs as new hedges on the Matrix (CR-20260724-001).
    //
    // The counterpart of previewCopilotClose, and the reason it had to exist: until now the only
    // CoPilot Preview that reached the Matrix was the close. New legs were routed to the Hedge Pad,
    // which holds INDEPENDENT in-memory state — so they were placed, and invisible here. The Adjust
    // engine emits every leg as "To Open" (Attribution is deferred), so this is the path every
    // Adjust Solution takes.
    //
    // ONE HEDGE PER CARD. A card is one recognisable structure the engine built for one purpose —
    // the cap, the lift — which is exactly what a Matrix hedge is, so the two map one to one and the
    // hedge takes the card's own name. It also makes the Matrix's single-side rule automatic: a card
    // is single-sided by construction.
    //
    // Without cards (the Close tab, the LLM path) there is no breakdown to honour, so the legs are
    // grouped BY SIDE instead — the most that can be inferred. That was the original behaviour and
    // it is why a two-card package used to land as one hedge.
    //
    // ALL-OR-NOTHING: every leg is resolved before anything is created, so a package that cannot be
    // placed leaves the Matrix byte-identical rather than half-built.
    // CR-20260924-002: the gap an ES-mode answer stamped on a card's legs (the same number on every
    // leg of one card, by construction); null when the card carries none (an SPX card).
    private copilotEsGapOf(legs: Leg[]): number | null {
        for (const leg of legs || []) {
            if (leg && isValidNumber(leg.esGap)) {
                return leg.esGap;
            }
        }
        return null;
    }

    async previewCopilotOpen(solutionId: string, legs: Leg[], cards?: PackageCard[], prePlacementGuard?: () => boolean): Promise<ApplyResult> {
        // WHICH REFUSAL, SAID OUT LOUD. Every exit below returns the same 'unplaceable-leg' reason,
        // and the panel turns all of them into one sentence — "a leg could not be placed" — so a
        // trader (and whoever is asked to fix it) cannot tell nine different causes apart. That cost
        // a whole diagnosis round on 2026-08-06: the failure was reasoned about from the code rather
        // than read off the run, and the theory was wrong. The toast stays as it is; this names the
        // exact exit for anyone who opens the console.
        const refuse = (where: string, detail?: unknown): ApplyResult => {
            console.warn('[CoPilot Preview REFUSED] ' + where, detail === undefined ? '' : detail);
            return { applied: false, reason: 'unplaceable-leg' };
        };

        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds) || isVoid(this.theGrid)) {
            return refuse('grid not ready / torn down');
        }
        if (isVoid(legs) || legs.length === 0) {
            return refuse('the solution carries no legs');
        }
        if (!isVoid(this._copilotOpenHedgesBySolution[solutionId])
            || !isVoid(this._copilotModifiedHedgesBySolution[solutionId])) {
            return { applied: false, reason: 'hedge-locked' };      // re-entrant preview of the same Solution
        }

        // STAGE 7 (Attribution) — cards that change a hedge the book ALREADY holds. They are resolved
        // first and mutated last, together with the new hedges, so the whole package is still
        // all-or-nothing: a target hedge the user is already editing must abort the entire Preview,
        // not leave half a package placed beside a refusal.
        const targets: { card: PackageCard; hedge: HedgeData }[] = [];
        const targetCards = (cards || []).filter(c => !isVoid(c.targetHedgeId));
        for (const card of targetCards) {
            const hedge = ds.getHedge(card.targetHedgeId);
            if (isVoid(hedge)) {
                return { applied: false, reason: 'missing-target-leg' };   // the hedge is gone
            }
            if (hedge.underModification === true) {
                // A manual modification, or a preserved divergence. CoPilot never overwrites the
                // trader's own in-progress edit — the same rule the close-Preview follows.
                return { applied: false, reason: 'hedge-modified' };
            }
            if (isVoid(card.targetOutcome) && card.closesTheHedge !== true) {
                // A target card without an after-state cannot be applied: the Matrix writes OUTCOMES,
                // and guessing one from the legs is exactly the derivation the engine sends it to
                // avoid. Refusing beats reshaping a held hedge into a shape nobody computed.
                //
                // ⚠ EXCEPT WHEN THE CARD CLOSES THE HEDGE, which is a real and common answer with a
                // genuinely empty outcome. That case used to land here and take the whole package
                // with it — protobuf-net writes an empty repeated field as ABSENT, so "left holding
                // nothing" and "no outcome computed" arrived identical. `closesTheHedge` is the
                // engine stating which, because the wire cannot carry the difference.
                return refuse('a card targets hedge ' + card.targetHedgeId
                    + ' but carries no targetOutcome and is not flagged as closing it', card.title);
            }
            targets.push({ card, hedge });
        }

        // The hedges to BUILD, in order. With cards that is one per card, named by the card; without
        // them it is one per side, which is the most a flat leg list can say about itself. Cards that
        // target an existing hedge are excluded — they change one, they do not create one.
        const sources: { name: string; legs: Leg[] }[] = [];
        if (!isVoid(cards) && cards.length > 0) {
            cards.filter(c => isVoid(c.targetHedgeId)).forEach(c => sources.push({ name: c.title, legs: c.legs || [] }));
        } else {
            // Refuse an unparseable ticker HERE: a leg with no readable side belongs to neither
            // group, so filtering alone would drop it silently and place a smaller trade than the
            // card promised. Phase 1 would never see it to object.
            if (legs.some(l => this.legSideOf(l) === null)) {
                return refuse('a leg has no readable side (unparseable ticker), cards absent',
                    legs.filter(l => this.legSideOf(l) === null).map(l => l.ticker));
            }
            const calls = legs.filter(l => this.legSideOf(l) === 'Call');
            const puts = legs.filter(l => this.legSideOf(l) === 'Put');
            if (calls.length > 0) {
                sources.push({ name: '* ORION *', legs: calls });
            }
            if (puts.length > 0) {
                sources.push({ name: '* ORION *', legs: puts });
            }
        }

        // PHASE 1 — resolve everything, mutate nothing. Strikes are kept as the ACTUAL strikes the
        // engine speaks; converting them happens in phase 2, after the underlying context is loaded
        // (the fair-value offset is only known then, and reading it early would silently yield 0).
        const resolved: {
            name: string;
            side: 'Call' | 'Put';
            underlying: string;
            legs: { actualStrike: number; expiration: string; qty: number }[];
            // CR-20260924-002: the fair-value gap the backend lens used for this card's contract
            // (stamped on its legs); null on an SPX card or an answer that carried none.
            esGap: number | null;
        }[] = [];

        for (const source of sources) {
            let side: 'Call' | 'Put' = null;
            let underlying: string = null;
            const placed: { actualStrike: number; expiration: string; qty: number }[] = [];

            for (const leg of source.legs) {
                if (leg.effect !== 'open') {
                    // A close / increase / decrease belongs to an EXISTING hedge, and that hedge is
                    // named on its card's targetHedgeId (stage 7). Reaching here means such a leg
                    // arrived on a card with no target, so there is nothing to apply it to —
                    // refusing beats guessing at a hedge nobody identified.
                    return refuse("card '" + source.name + "' creates a NEW hedge but carries a '"
                        + leg.effect + "' leg", leg.ticker);
                }
                const parsed = parseOptionTicker(leg.ticker);
                if (isVoid(parsed) || isVoid(parsed.underlying) || isVoid(parsed.expiration)
                    || !isValidNumber(parsed.strike) || (parsed.type !== 'Call' && parsed.type !== 'Put')) {
                    return refuse("card '" + source.name + "' has an unparseable ticker", leg.ticker);
                }

                // A Matrix hedge is single-sided and single-instrument. A card is too, by
                // construction — so a card that is not says the breakdown itself is wrong, and
                // placing it would build a hedge the grid cannot represent.
                if (side === null) {
                    side = parsed.type;
                    underlying = parsed.underlying;
                } else if (side !== parsed.type || underlying !== parsed.underlying) {
                    return refuse("card '" + source.name + "' mixes sides or underlyings ("
                        + side + '/' + underlying + ' vs ' + parsed.type + '/' + parsed.underlying
                        + ') - a Matrix hedge is single-sided', leg.ticker);
                }

                // Quantity is ABSOLUTE here, deliberately NOT multiplied by the default quantity the
                // template path applies: template legs are ratios per default qty, engine legs are real
                // contract counts. Multiplying again would silently place the package at 10x size.
                const qty = leg.side === 'sell' ? -Math.abs(leg.quantity) : Math.abs(leg.quantity);
                if (qty !== 0) {
                    placed.push({ actualStrike: parsed.strike, expiration: parsed.expiration, qty });
                }
            }

            // A card whose legs all cancelled carries nothing to place — it is simply not a hedge.
            if (placed.length > 0) {
                resolved.push({ name: source.name, side, underlying, legs: placed, esGap: this.copilotEsGapOf(source.legs) });
            }
        }

        // A package may now legitimately create NOTHING — a fully attributed one changes hedges the
        // book already holds and builds none. It is only empty if there is also nothing to change.
        if (resolved.length === 0 && targets.length === 0) {
            return refuse('nothing to place and nothing to change - every card resolved to nothing');
        }

        // PHASE 1b (CR-20260924-002) — the ES silos. An ES card lands in its contract's silo,
        // created HERE when missing, on the gap the backend lens used — never a second fair-value
        // read that could land five points away — and a silo already built on another gap refuses.
        // Every context is ensured BEFORE anything is modified or created, so a refusal here leaves
        // nothing behind and phase 2 keeps its no-partial-failure guarantee. An ES hedge while the
        // matrix's own ES hedging is OFF has no column to show in: refused, never placed blind.
        const baseUnderlying = ds.getPortfolioUnderlying();
        for (const group of resolved) {
            if (group.underlying === baseUnderlying) {
                continue;
            }
            if (!this.esHedgingEnabled) {
                return refuse('ES hedging is off on the Hedge Matrix - an ES hedge has no column to land in', group.name);
            }
            const ready = await ds.ensureUnderlyingContext(group.underlying, undefined,
                isValidNumber(group.esGap) ? group.esGap : undefined);
            if (!ready) {
                return refuse('the ES silo for ' + group.underlying
                    + ' could not be prepared (fair value unavailable, or it changed since the run)', group.name);
            }
        }

        // CR-20260901-005 round 4: NO history clear — manual-edit transactions survive preview
        // transitions (owner ruling); these preview writes are unrecorded, and an undo landing on
        // a previewed hedge later goes through the divergence path like any manual edit.

        // PHASE 2a — change the existing hedges. Same mechanics as a previewed close (enter
        // modification, reveal the Trans/Outcome columns, write outcomes), but the outcome is the
        // engine's after-state instead of a flat zero.
        const modifiedIds: string[] = [];
        for (const target of targets) {
            if (!await this.applyCopilotOutcome(target.hedge, target.card, prePlacementGuard)) {
                // Unreachable after phase 1, which resolved every target — but a partial package is
                // the one state this method must never leave behind, so it unwinds rather than
                // trusting that.
                modifiedIds.forEach(id => this.revertCopilotModifiedHedge(id));
                return refuse('applyCopilotOutcome failed for hedge ' + target.hedge.id,
                    target.card.title);
            }
            modifiedIds.push(target.hedge.id);
            this._copilotPreviewedHedgeIds.add(target.hedge.id);
        }

        if (modifiedIds.length > 0) {
            this._copilotModifiedHedgesBySolution[solutionId] = modifiedIds;
        }

        // PHASE 2 — create. addNewHedge is a pure in-memory add and setTransQty cannot fail, so
        // there is no partial-failure state to unwind past this point.
        const createdIds: string[] = [];
        for (const group of resolved) {
            await ds.ensureUnderlyingContext(group.underlying);

            const hedge = await ds.addNewHedge(group.side, group.underlying);
            hedge.name = group.name;

            // The engine speaks ACTUAL strikes; the cell index is keyed by the SPX-EQUIVALENT
            // strike, with actualStrike derived back off the fair-value offset. For the base
            // underlying the two are the same number, which is why this only bites on ES.
            const offset = ds.getFairValueOffset(group.underlying);
            group.legs.forEach(l => ds.setTransQty(hedge, l.actualStrike - offset, l.expiration, l.qty));

            this.subscribeHedgeQuotes(hedge);

            createdIds.push(hedge.id);
            this._copilotPreviewedHedgeIds.add(hedge.id);   // divergence tracking, same as a previewed close
        }

        if (createdIds.length > 0) {
            this._copilotOpenHedgesBySolution[solutionId] = createdIds;
        }

        // ROWS, not just columns — this is the step whose absence made the first build add an empty
        // column. A hedge column renders a cell only where a ROW exists for that strike, and the row
        // set is the displayed ladder UNIONED WITH the hedges' own leg strikes (getStrikesToShow).
        // Engine legs land wherever the shortfall bends, which is regularly off the ±N-strike ladder
        // the trader is looking at, so the rows have to be rebuilt after the cells exist.
        //
        // The manual add never needed this: a template hedge is anchored on a row the user clicked.
        //
        // rebuildRows also re-applies the column defs (picking up the new hedge's own column) and
        // re-runs the P&L over the new strike set, so it replaces the per-hedge column calls rather
        // than adding to them - doing both would set the column defs twice and fight over state.
        // A hedge this Preview put under modification keeps its Trans/Outcome columns across this
        // rebuild because syncModificationColumns re-derives their visibility from the hedge itself.
        // Nothing is re-asserted here: see that method for why the call site is the wrong place.
        this.rebuildRows();
        return { applied: true };
    }

    // CoPilot stage 7 — reshape ONE existing hedge into the engine's after-state.
    //
    // The Matrix already had this operation: a previewed close is the same move with an after-state
    // of nothing. So this reuses the exact mechanics a manual "Modify Hedge" produces — enter
    // modification, reveal the Trans/Outcome columns, write OUTCOMES — and lets the data service
    // derive each transaction as (outcome - held), which is where that arithmetic already lives.
    //
    // Two things it does NOT do, deliberately: it never multiplies by the default quantity (engine
    // holdings are real contract counts, not template ratios), and it reads the fair-value offset
    // only AFTER the underlying context is loaded, because before that the offset reads 0 — correct
    // for the base underlying and silently wrong for ES.
    private async applyCopilotOutcome(hedge: HedgeData, card: PackageCard, prePlacementGuard?: () => boolean): Promise<boolean> {
        const ds = this.hedgeMatrixDataService;
        const silo = hedge.underlying || ds.getPortfolioUnderlying();

        await ds.ensureUnderlyingContext(silo);
        // Automatic adjustments validate the still-loaded owner/source and manual/preview conflicts
        // after asynchronous preparation, immediately before the synchronous mutation below.
        if (prePlacementGuard && !prePlacementGuard()) { return false; }
        const offset = ds.getFairValueOffset(silo);

        // The engine speaks ACTUAL strikes; Matrix cells are keyed by the SPX-equivalent one.
        // An outcome of NOTHING is a real after-state: the card closes the hedge. It arrives as
        // `undefined` rather than `[]` because protobuf-net writes an empty repeated field as
        // absent, so the caller vouches for it with `closesTheHedge` and this loop simply has
        // nothing to walk. The clearing pass below then closes every held cell, which is exactly
        // the transaction a manual close writes.
        const wanted: { strike: number; expiration: string; qty: number }[] = [];
        for (const holding of card.targetOutcome || []) {
            const parsed = parseOptionTicker(holding.ticker);
            if (isVoid(parsed) || !isValidNumber(parsed.strike) || isVoid(parsed.expiration)) {
                return false;
            }
            // A hedge is ONE column and one side. Cells are addressed by strike + expiration only,
            // so an outcome holding of the OTHER side does not fail below — it silently becomes a
            // leg of THIS hedge's side at that strike: a different instrument, at a different
            // price, from the one the card priced. Refusing leaves the Matrix untouched, which is
            // the only honest answer to an after-state this column cannot hold. (QA 2026-08-03 —
            // the engine no longer emits one; this is the surface that made it invisible.)
            if (parsed.type !== hedge.type) {
                return false;
            }
            wanted.push({
                strike: parsed.strike - offset,
                expiration: parsed.expiration,
                qty: holding.quantity            // SIGNED — a holding carries its own side
            });
        }

        this.setCopilotHedgeModColumns(silo, hedge.id, true);
        ds.onHedgeModificationStarted(hedge);    // seeds outcomeQty = qty, underModification = true

        // Anything the hedge HOLDS that the after-state does not keep is closed. Clearing the
        // outcome (NaN) is what the data service turns into transQty = -legQty, which is the same
        // closing transaction the manual punch-pad writes.
        (ds.getCellDataForHedge(hedge.id) || []).forEach(cd => {
            if (typeof cd.qty !== 'number' || isNaN(cd.qty) || cd.qty === 0) {
                return;
            }
            const kept = wanted.some(w => Math.abs(w.strike - cd.strike) < 1e-6 && w.expiration === cd.expiration);
            if (!kept) {
                ds.setOutcomeQty(hedge, cd.strike, cd.expiration, Number.NaN);
            }
        });

        // ... and everything it keeps is written to its new size. A strike the hedge did not hold
        // is created by the setter, which is how a fold ADDS a leg to an existing hedge.
        wanted.forEach(w => ds.setOutcomeQty(hedge, w.strike, w.expiration, w.qty));

        // A fold can add a contract this hedge never held, and an unsubscribed cell prices as blank.
        // subscribeIfNotYet makes this safe to call on a hedge that is already subscribed.
        this.subscribeHedgeQuotes(hedge);

        return true;
    }

    // Return one modified hedge to its held state — the same exit unPreviewCopilotClose uses. NEVER
    // deletes: the trader owned this hedge before the Preview and must still own it after.
    private revertCopilotModifiedHedge(hedgeId: string): void {
        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds)) {
            return;
        }
        const hedge = ds.getHedge(hedgeId);
        if (isVoid(hedge)) {
            return;
        }
        ds.onHedgeModificationFinished(hedge);
        this.setCopilotHedgeModColumns(hedge.underlying || ds.getPortfolioUnderlying(), hedge.id, false);
    }

    /// The side a leg trades, read off its ticker. Null when the ticker is not a parseable option
    /// ticker — the caller refuses those rather than guessing a side for them.
    private legSideOf(leg: Leg): 'Call' | 'Put' | null {
        const parsed = parseOptionTicker(leg ? leg.ticker : null);
        if (isVoid(parsed) || (parsed.type !== 'Call' && parsed.type !== 'Put')) {
            return null;
        }
        return parsed.type;
    }

    // CoPilot — un-Preview new legs: delete the hedges this Solution created. A DIVERGED hedge (the
    // user edited it after previewing) is left in place, exactly as unPreviewCopilotClose preserves
    // a diverged close — the trader's own edit is never thrown away by a CoPilot toggle.
    unPreviewCopilotOpen(solutionId: string): void {
        (this._copilotModifiedHedgesBySolution[solutionId] || []).forEach(id => delete this._availableAdjustmentByHedge[id]);
        // CR-20260901-005 round 4: no history clear — see previewCopilotOpen. A non-diverged
        // created hedge removed below can carry no manual-edit transactions (editing it would have
        // marked it diverged, and diverged hedges are left in place).
        // Stage 7: a package may have CHANGED hedges as well as created them, and the two are undone
        // differently — created hedges are deleted, changed ones are returned to their held state.
        // A diverged hedge is left alone on both paths: the trader edited it after previewing, and a
        // CoPilot toggle never discards the trader's own work.
        const modifiedIds = this._copilotModifiedHedgesBySolution[solutionId];
        if (!isVoid(modifiedIds)) {
            delete this._copilotModifiedHedgesBySolution[solutionId];
            modifiedIds.forEach(hedgeId => {
                const wasDiverged = this._copilotDivergedHedgeIds.has(hedgeId);
                this._copilotPreviewedHedgeIds.delete(hedgeId);
                this._copilotDivergedHedgeIds.delete(hedgeId);
                if (!wasDiverged && !isVoid(this.theGrid)) {
                    this.revertCopilotModifiedHedge(hedgeId);
                }
            });
            if (!isVoid(this.theGrid)) {
                this.recalculatePnls();
                this.theGrid.api.refreshCells({ force: true });
            }
        }

        const createdIds = this._copilotOpenHedgesBySolution[solutionId];
        if (isVoid(createdIds)) {
            return;
        }
        delete this._copilotOpenHedgesBySolution[solutionId];

        let removedAny = false;
        createdIds.forEach(hedgeId => {
            const wasDiverged = this._copilotDivergedHedgeIds.has(hedgeId);
            this._copilotPreviewedHedgeIds.delete(hedgeId);
            this._copilotDivergedHedgeIds.delete(hedgeId);

            if (wasDiverged || isVoid(this.hedgeMatrixDataService) || isVoid(this.theGrid)) {
                return;
            }
            this.hedgeMatrixDataService.removeHedge(hedgeId);
            removedAny = true;
        });

        // One rebuild for the whole solution, not one per hedge: the ladder shrinks back by the
        // strikes those hedges were holding open, and rebuilding rows re-applies the column defs too.
        if (removedAny && !isVoid(this.theGrid)) {
            this.rebuildRows();
            this.removeOrphanCompanionRows();
        }
    }

    // CoPilot — un-Preview a close: exit modification mode (nulls trans/outcome, underModification=false),
    // hide the Trans/Outcome columns, and repaint. Returns the hedge to its pristine held state.
    unPreviewCopilotClose(hedgeId: string): void {
        // CR-20260901-005 round 4: no history clear — see previewCopilotOpen.
        // Drop CoPilot ownership/divergence tracking FIRST, so a torn-down grid (early return below) does
        // not leave stale Set entries.
        const wasDiverged = this._copilotDivergedHedgeIds.has(hedgeId);
        this._copilotPreviewedHedgeIds.delete(hedgeId);
        this._copilotDivergedHedgeIds.delete(hedgeId);

        const ds = this.hedgeMatrixDataService;
        if (isVoid(ds) || isVoid(this.theGrid)) {
            return;   // grid not ready / torn down — tracking already cleared; state rebuilds on reload
        }
        const hedge = ds.getHedge(hedgeId);
        if (isVoid(hedge)) {
            return;
        }
        if (wasDiverged) {
            // M2: the user manually edited this previewed hedge — leave their custom modification intact
            // (do NOT revert). The hedge is now an ordinary user modification, like a manual close.
            return;
        }
        ds.onHedgeModificationFinished(hedge);
        const silo = hedge.underlying || ds.getPortfolioUnderlying();
        this.setCopilotHedgeModColumns(silo, hedge.id, false);
        this.recalculatePnls();
        this.theGrid.api.refreshCells({ force: true });
    }

    private setCopilotHedgeModColumns(silo: string, hedgeId: string, visible: boolean): void {
        const transCol = this.theGrid.columnApi.getColumn(buildHedgeColId(silo, hedgeId, 'trans'));
        if (!isVoid(transCol)) {
            this.theGrid.columnApi.setColumnVisible(transCol, visible);
        }
        const outcomeCol = this.theGrid.columnApi.getColumn(buildHedgeColId(silo, hedgeId, 'outcome'));
        if (!isVoid(outcomeCol)) {
            this.theGrid.columnApi.setColumnVisible(outcomeCol, visible);
        }
    }

    async addHedge(
        side: "Call" | "Put",
        templateStrategyId?: string,
        rowData?: HedgeMatrixRow,
        insertAtColId?: string,
        insertSide: 'left' | 'right' = 'left',
        sourceUnderlying?: string,
        sourceExpiration?: string
    ) {
        this._pendingInsertAtColId = insertAtColId || null;
        this._pendingInsertSide = insertSide;
        console.log('[addHedge] START. pendingInsert:', insertAtColId, insertSide);

        let selectedUnderlying: string;

        if (sourceUnderlying) {
            // Inherit underlying from the hedge that was right-clicked
            selectedUnderlying = sourceUnderlying;
        } else if (this.esHedgingEnabled) {
            const baseUnderlying = await this.getPortfolioUnderlying(this.selectedPortfolio);
            console.log('[addHedge] AFTER getPortfolioUnderlying await. pendingInsert:', this._pendingInsertAtColId);
            selectedUnderlying = await this.underlyingPicker.show(baseUnderlying);
            console.log('[addHedge] AFTER underlyingPicker await. pendingInsert:', this._pendingInsertAtColId);
            if (!selectedUnderlying) {
                return;
            }
        } else {
            selectedUnderlying = await this.getPortfolioUnderlying(this.selectedPortfolio);
            console.log('[addHedge] AFTER getPortfolioUnderlying await. pendingInsert:', this._pendingInsertAtColId);
        }

        console.log('[addHedge] BEFORE addHedgeInternal. pendingInsert:', this._pendingInsertAtColId);
        // The first hedge on a new underlying (e.g. the first ES hedge) fetches that underlying's
        // option chain + fair value from the backend and then builds its column set — several
        // seconds with nothing visible. Show the load panel for the whole add.
        this.isLoading = true;
        this._changeDetector.detectChanges();
        try {
            await this.addHedgeInternal(side, selectedUnderlying, templateStrategyId, rowData, sourceExpiration);
        } finally {
            this.fillReferenceLists();
            this.isLoading = false;
            this._changeDetector.detectChanges();
        }
        console.log('[addHedge] DONE. pendingInsert:', this._pendingInsertAtColId);
    }

    private async addHedgeInternal(side: "Call" | "Put", underlying: string, templateStrategyId?: string, rowData?: HedgeMatrixRow, sourceExpiration?: string): Promise<HedgeData> {

        // CR-20260901-005: hedge creation (manual add, template drop, paste) is a structural
        // boundary — hedge identity is outside the snapshot, so history cannot bridge it.
        this.clearMatrixUndoHistory();

        const ul = underlying;

        const defaultQty = this.portfolioDefaultQty;

        // Ensure underlying context is loaded (needed for ES on empty matrix)
        await this.hedgeMatrixDataService.ensureUnderlyingContext(ul);

        const addedHedge = await this.hedgeMatrixDataService
            .addNewHedge(side, ul);

        if (sourceExpiration) {
            addedHedge.initialExpiration = sourceExpiration;
        }

        if (!isVoid(templateStrategyId)) {
            try {

                if (isVoid(rowData)) {
                    const errMsg = 'Data For Selected Row Not Found';
                    this._toastr.error(errMsg);
                    throw new Error(errMsg)
                }

                let templates = side === 'Call'
                    ? this._opgTemplatesService.getCallTemplates(this.selectedPortfolio.userId)
                    : this._opgTemplatesService.getPutTemplates(this.selectedPortfolio.userId);

                const descriptor = templates
                    .flatMap(x => x.descriptors)
                    .find(x => x.strategyId === templateStrategyId);

                if (isVoid(descriptor)) {
                    const errMsg = 'Selected Hedge Template Not Found';
                    this._toastr.error(errMsg);
                    throw new Error(errMsg)
                }

                const chain = await this._optionsChainService.getChain(ul);

                addedHedge.template = descriptor;

                let baseStrike = rowData.strike;

                let baseExpiration = this.hedgeMatrixDataService
                    .getHedgeDefaultExpiration(addedHedge.id);

                let baseExpirationDescriptor = chain.expirations
                    .find(x => x.optionExpirationDate === baseExpiration);

                const strikeMultiplier = side === 'Call' ? 1 : -1;

                let legs = descriptor.strategyLegs.slice();

                if (side === 'Call') {
                    legs.reverse();
                }

                const hasNegativeOffsets = legs
                    .filter(x => x.type === 'leg')
                    .some(l => l.expirationOffset < 0);

                if (hasNegativeOffsets) {
                    const offsets = legs.filter(x => x.type === 'leg')
                        .map(x => x.expirationOffset || 0);

                    const maxNegativeOffset = Math.min(...offsets);

                    const baseExpirationDateTime = DateTime.fromISO(baseExpiration);

                    const targetTime = baseExpirationDateTime.plusBusiness({days: Math.abs(maxNegativeOffset)});

                    const diff = targetTime.diff(baseExpirationDateTime, 'days').toObject().days;

                    const bdAdjustedDte = diff + baseExpirationDescriptor.daysToExpiration;

                    baseExpirationDescriptor = chain.expirations
                        .find(x => x.daysToExpiration >= bdAdjustedDte);
                }

                addedHedge.anchorExpiration = baseExpirationDescriptor;

                for (let leg of legs) {
                    if (leg.type === 'width') {
                        baseStrike += (leg.width * strikeMultiplier);
                    } else {
                        let expiration = baseExpirationDescriptor.optionExpirationDate;

                        const qtyMultiplier = leg.side === 'Buy' ? 1 : -1;

                        if (isValidNumber(leg.expirationOffset)) {
                            const offsetExpiration = findOffsetExpiration(
                                baseExpirationDescriptor,
                                leg.expirationOffset,
                                chain
                            );

                            if (isVoid(offsetExpiration)) {
                                const errMsg = `One (or more) of the legs of the chosen calendarized template 
                            are referencing non-existing expirations. Check selected negative offsets.`;
                                this._toastr.error(errMsg);
                                throw new Error()
                            }

                            expiration = offsetExpiration.optionExpirationDate;
                        }

                        this.hedgeMatrixDataService.setTransQty(
                            addedHedge,
                            baseStrike,
                            expiration,
                            leg.qty * defaultQty * qtyMultiplier
                        );
                    }
                }
            } catch {
                this.hedgeMatrixDataService.removeHedge(addedHedge.id);
                return;
            }
        }

        this.subscribeHedgeQuotes(addedHedge);

        // First hedge in the grid — full rebuild to create pricing/P&L columns
        // Otherwise — incremental column addition
        const allHedges = this.hedgeMatrixDataService.getHedges();
        if (allHedges.length === 1) {
            this.rebuildColumns();
        } else {
            this.addHedgeColumns(addedHedge);
        }


        return addedHedge;
    }

    private subscribeHedgeQuotes(hedge: HedgeData) {
        const cellData = this.hedgeMatrixDataService.getCellDataForHedge(hedge.id);
        if (!cellData?.length) return;
        cellData.forEach(cd => {
            if (cd.ticker) {
                this.lastQuoteCache.subscribeIfNotYet(cd.ticker);
            }
        });
    }

    deleteHedge(hedgeData: HedgeData) {
        this._messageBus.publishAsync({
            topic: 'Hg.RequestDeleteHedge',
            payload: {
                hedgeId: hedgeData.id,
                hedgeName: hedgeData.name,
                side: hedgeData.type
            }
        });
    }

    removeHedge(id: string) {
        // CR-20260901-005: structural boundary, same as addHedgeInternal.
        this.clearMatrixUndoHistory();
        this.hedgeMatrixDataService.removeHedge(id);
        this.rebuildColumns();
        this.removeOrphanCompanionRows();
    }

    private removeOrphanCompanionRows() {
        const allRows: HedgeMatrixRow[] = [];
        this.theGrid.api.forEachNode(n => allRows.push(n.data));

        const hedges = this.hedgeMatrixDataService.getHedges();
        const doubleStrikes = new Set<number>();
        hedges.forEach(h => {
            const cellData = this.hedgeMatrixDataService.getCellDataForHedge(h.id);
            const groups = {};
            (cellData || []).forEach(cd => {
                groups[cd.strike] = (groups[cd.strike] || 0) + 1;
            });
            Object.keys(groups).forEach(s => {
                if (groups[s] > 1) doubleStrikes.add(parseInt(s, 10));
            });
        });

        const toAdd: HedgeMatrixRow[] = [];
        const toRemove: HedgeMatrixRow[] = [];
        let mutated = false;

        allRows.forEach(row => {
            if (row.isPinned || row.strike == null) return;
            const isCompanion = row.rowId && row.rowId.includes('-');
            const shouldSpan = doubleStrikes.has(row.strike);

            if (isCompanion && !shouldSpan) {
                toRemove.push(row);
            } else if (!isCompanion) {
                if (shouldSpan && row.rowSpan !== 2) {
                    row.rowSpan = 2;
                    row.isMultiExpiration = true;
                    mutated = true;
                    const companionId = row.strike + '-1';
                    if (!allRows.some(r => r.rowId === companionId)) {
                        toAdd.push({
                            rowId: companionId,
                            strike: row.strike,
                            rowSpan: 1,
                            isMultiExpiration: true
                        });
                    }
                } else if (!shouldSpan && row.rowSpan === 2) {
                    row.rowSpan = 1;
                    row.isMultiExpiration = false;
                    mutated = true;
                }
            }
        });

        if (toAdd.length > 0 || toRemove.length > 0) {
            this.theGrid.api.applyTransaction({ add: toAdd, remove: toRemove });
            const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
            this.theGrid.api.setSortModel([
                { colId: buildStrikeColId(baseSilo), sort: 'desc' }
            ]);
        }

        if (mutated || toAdd.length > 0 || toRemove.length > 0) {
            this.theGrid.api.redrawRows();
        }

        console.log('[HM] removeOrphanCompanionRows', { added: toAdd.length, removed: toRemove.length, mutated });
    }

    async copyOrders(hedge: HedgeData, destination: 'tos' | 'other-panel', source: 'qty' | 'trans' | 'outcome' = 'trans') {
        const legs = this._sourceLegs(hedge.id, source);

        if (!legs || legs.length === 0) {
            this._toastr.warning(`Cannot copy ${source} for "${hedge.name}": no legs available`);
            return;
        }

        // For 'trans' source, keep existing modification gate; for qty/outcome, source legs always usable.
        if (source === 'trans') {
            const cellData = this._hedgeMatrixDataService.getCellDataForHedge(hedge.id);
            const hasTransactions = cellData.some(function(x) { return isValidNumber(x.transQty); });
            if (!hasTransactions) {
                this._toastr.warning('Cannot copy orders for "' + hedge.name + '": has no changes');
                return;
            }
        }

        this.showVerificationDialog(hedge, source)
            .then(async () => {
                if (destination === 'tos') {
                    await this.copyToTos(hedge, source);
                } else {
                    this.copyToEts(hedge, source);
                }
            })
            .catch(() => {
                // ignore
            });
    }

    private _sourceLegs(hedgeId: string, source: 'qty' | 'trans' | 'outcome') {
        if (source === 'qty') {
            const hedge = this._hedgeMatrixDataService.getHedge(hedgeId);
            if (hedge?.isNew) {
                return this._hedgeMatrixDataService.getTransactionLegs(hedgeId);
            }
            return this._hedgeMatrixDataService.getOriginalLegs(hedgeId);
        } else if (source === 'outcome') {
            return this._hedgeMatrixDataService.getNetOutcomeLegs(hedgeId);
        }
        return this._hedgeMatrixDataService.getTransactionLegs(hedgeId);
    }

    private async showVerificationDialog(hedge: HedgeData, source: 'qty' | 'trans' | 'outcome' = 'trans'): Promise<void> {
        const comboItems = this.makeComboItems(hedge.id, source);

        const orderPx = this.hedgeMatrixDataService.getTransCost(hedge.id) / this.portfolioDefaultQty;

        const config: OrdersVerificationDialogConfig = {
            orderLegs: comboItems.sort((a, b) => b.ticker.localeCompare(a.ticker)),
            orderPrice: orderPx,
            defaultQty: this.portfolioDefaultQty,
            ledgerPrice: () => this.hedgeMatrixDataService.getTransCost(hedge.id),
            groupName: hedge.name,
            groupColor: hedge.color,
        };

        // CR-20260827-001: opt in to the "Optimize Grouping" preview (owner: offer it for a
        // single hedge as well - "whatever configuration it is, act on this configuration").
        return this.orderVerificationDialog.show([config], undefined, undefined, true,
            (legs, qty, md) => this.pricingStepsPopup.show(legs, qty, md));
    }

    private async copyToTos(hedge: HedgeData, source: 'qty' | 'trans' | 'outcome' = 'trans') {

        const cbItems = this.makeComboItems(hedge?.id, source);

        if (isVoid(cbItems)) {
            this._toastr.warning('No Legs to Copy!');
            return;
        }

        if (cbItems.some(function(x) { return !x.ticker; })) {
            this._toastr.error('Some legs have invalid tickers. Check hedge expiration.');
            return;
        }

        const firstOptionTicker = parseOptionTicker(cbItems[0].ticker);
        const underlying = firstOptionTicker.underlying;

        const futuresItems = cbItems.filter(x => isFuturesTicker(parseOptionTicker(x.ticker).underlying));
        if (futuresItems.length > 0 && futuresItems.length !== cbItems.length) {
            this._toastr.error('Mixed futures and equity legs are not supported in a single TOS order');
            return;
        }

        let line: string;

        if (futuresItems.length === cbItems.length) {
            // Futures path
            const uniqueExpirations = new Set(cbItems.map(x => parseOptionTicker(x.ticker).expiration));
            if (uniqueExpirations.size > 1) {
                this._toastr.error('Multi-expiration combos are not supported yet for ES options in TOS Desktop format');
                return;
            }

            const hcf = findHCF(cbItems.map(x => x.netPosition));
            const totalQty = hcf;

            const transformedLegs = cbItems.map(x => {
                const optionTicker = parseOptionTicker(x.ticker);
                return {
                    qty: (x.netPosition / hcf) * (x.side as number),
                    date: DateTime.fromFormat(optionTicker.expiration, 'yyyy-MM-dd').toFormat('d MMM yy').toUpperCase(),
                    strike: optionTicker.strike,
                    type: optionTicker.type.toUpperCase(),
                };
            });

            const expDate = DateTime.fromFormat(firstOptionTicker.expiration, 'yyyy-MM-dd');

            line = makeFuturesTosDesktopLine({
                expDate: expDate as any,
                underlying,
                strikes: transformedLegs.map(x => x.strike),
                types: transformedLegs.map(x => x.type),
                legQtys: transformedLegs.map(x => x.qty),
                totalQty,
                dates: transformedLegs.map(x => x.date),
                price: ''
            });
        } else {
            // Equity path
            const portfolioUnderlying = this.hedgeMatrixDataService.getPortfolioUnderlying();

            const transformedLegs = cbItems.map(x => {
                const optionTicker = parseOptionTicker(x.ticker);
                return {
                    qty: x.netPosition * (x.side as number),
                    date: DateTime.fromFormat(optionTicker.expiration, 'yyyy-MM-dd').toFormat('dd MMM yy').toUpperCase(),
                    strike: optionTicker.strike,
                    type: optionTicker.type.toUpperCase(),
                };
            });

            const quantities = transformedLegs.map(x => x.qty).join('/');
            const dates = transformedLegs.map(x => x.date).join('/');
            const strikes = transformedLegs.map(x => x.strike).join('/');
            const types = transformedLegs.map(x => x.type).join('/');

            if (portfolioUnderlying === 'SPX') {
                line = `BUY 1 ${quantities} CUSTOM ${portfolioUnderlying} 100 (Weeklys) ${dates} ${strikes} ${types} @ LMT GTC`;
            } else {
                line = `BUY 1 ${quantities} CUSTOM ${portfolioUnderlying} 100 ${dates} ${strikes} ${types} @ LMT GTC`;
            }
        }

        await navigator.clipboard.writeText(line);

        this._toastr.success('Orders Copied to Clipboard!');
    }

    private copyToEts(hedge: HedgeData, source: 'qty' | 'trans' | 'outcome' = 'trans') {

        const comboItems = this.makeComboItems(hedge.id, source);

        if (isVoid(comboItems)) {
            this._toastr.warning('No Legs to Copy!');
            return;
        }

        const cost = this.hedgeMatrixDataService.getTransCost(hedge?.id);

        const msg: ComboHighlightedUIMessage = {
            items: comboItems,
            orderParams: {
                orderLimitPx: cost,
                orderDuration: TimeInForce.GTC,
                orderQty: 1,
                orderType: OrderType.Limit
            }
        };

        if (!hedge.isNew) {
            msg.hedgingGridTransaction = {
                groupId: hedge?.id
            }
        }


        this._clipboardService.put(ETS_CLIPBOARD_LEGS_KEY, msg);
        console.log(`[ETS-CLIPBOARD] write source=hm via=copyToEts items=${comboItems.length} hedgeSource=${source}`);
        this._toastr.success('Combo copied to clipboard');

    }

    private makeComboItems(hedgeId: string, source: 'qty' | 'trans' | 'outcome' = 'trans'): ComboHighlightedItem[] {

        const legs = this._sourceLegs(hedgeId, source);

        const comboItems = legs.map(x => {

            const optionTicker = parseOptionTicker(x.ticker);

            const displayName = this._displayNameService.getOptionDisplayName(x.ticker);

            const optionType = optionTicker.type === 'Call'
                ? PortfolioItemType.Call
                : PortfolioItemType.Put;

            let side = MarketSide.Flat;
            if (x.qty > 0) {
                side = MarketSide.Buy;
            } else if (x.qty < 0) {
                side = MarketSide.Sell;
            }

            const item: ComboHighlightedItem = {
                accountId: null,
                comboId: null,
                portfolioId: null,
                ticker: x.ticker,
                underlying: optionTicker.underlying,
                tickerDisplayName: displayName,
                netPosition: Math.abs(x.qty),
                side: side,
                itemType: optionType,
                strategyId: null
            };

            return item;
        });

        return comboItems;
    }

    @DetectMethodChanges()
    onPnlExpirationSelectionChanged(expiration: {
        expiration: string,
        side: 'Call' | 'Put'
    }, value?: boolean, allInclusive?: boolean, silo?: string) {

        const resolvedSilo = silo || this.hedgeMatrixDataService.getPortfolioUnderlying();
        const id = buildPnlExpirationColId(resolvedSilo, expiration.expiration, expiration.side, allInclusive);

        const column = this.theGrid.columnApi.getColumn(id);

        if (isVoid(column)) {
            return;
        }

        if (isNullOrUndefined(value)) {
            value = !column.isVisible();
        }

        this.theGrid.columnApi.setColumnVisible(column, value);

        if (value) {
            this.recalculatePnls();
        }

        this.theGrid.api.refreshCells({force: true});

    }

    updateVisibleColumns() {

        if (!this.theGrid || this._isRestoringGridState) {
            return;
        }

        this._saveGridState$.next();

    }

    private saveGridState() {
        const gridOptions = this.theGrid;

        const colState = gridOptions.columnApi.getColumnState();
        const groupState = gridOptions.columnApi.getColumnGroupState();
        const expirationPnlNormalizedState = this.expirationPnlShowNormalized.slice();

        const pfKey = makePortfolioKey(this.selectedPortfolio);

        const gridState = {colState, groupState, expirationPnlNormalizedState};

        this._userSettingsService.setValue(`hg.matrix.columns.${pfKey}`, gridState);
    }

    clearSavedGridState() {
        const pfKey = makePortfolioKey(this.selectedPortfolio);
        const key = `hg.matrix.columns.${pfKey}`;
        this._userSettingsService.setValue(key, {});
    }

    resetGridState() {
        // Snapshot current column visibility before reset
        const visibleColIds = new Set<string>();
        if (this.theGrid) {
            const colState = this.theGrid.columnApi.getColumnState();
            colState.forEach(function(c) {
                if (!c.hide) {
                    visibleColIds.add(c.colId);
                }
            });
        }

        this.clearSavedGridState();
        this.recreateGrid();

        // Re-apply visibility: columns that were visible before stay visible
        if (this.theGrid && visibleColIds.size > 0) {
            const newState = this.theGrid.columnApi.getColumnState();
            newState.forEach(function(c) {
                if (visibleColIds.has(c.colId)) {
                    c.hide = false;
                }
            });
            this._isRestoringGridState = true;
            try {
                this.theGrid.columnApi.setColumnState(newState);
            } finally {
                this._isRestoringGridState = false;
            }
            this.saveGridState();
        }
    }

    dumpGridState() {
        console.log('[GridState] current:', this.theGrid.columnApi.getColumnState());
    }

    dumpSavedGridState() {
        console.log('[GridState] saved:', this.loadSavedGridState());
    }

    private sortByFuturesExpiration(tickers: string[]) {
        const monthOrder = 'FGHJKMNQUVXZ';
        tickers.sort((a, b) => {
            const aMonth = monthOrder.indexOf(a.charAt(a.length - 2));
            const bMonth = monthOrder.indexOf(b.charAt(b.length - 2));
            if (aMonth !== bMonth) return aMonth - bMonth;
            return a.localeCompare(b);
        });
    }

    private applyColumnDefs(columnDefs: any[]) {
        this._isRestoringGridState = true;
        try {
            // 1. Build default column state from column defs (preserves getColumnDefs() order)
            const defaultColState: ColumnState[] = [];
            this.flattenColumnDefs(columnDefs, defaultColState);
            const validColIds = new Set(defaultColState.map(c => c.colId));

            // 2. Load saved state
            const savedState: GridState = this.loadSavedGridState() || {};

            // 2a. Remap acquired hedge IDs (old local ID → new ID) before filtering
            if (savedState.colState && this._acquiredHedgeIdMap.size > 0) {
                const allHedges = this.hedgeMatrixDataService?.getHedges() || [];
                const nameToNewId = new Map<string, string>();
                allHedges.forEach(h => nameToNewId.set(h.name, h.id));

                const expandedState: any[] = [];
                savedState.colState.forEach(col => {
                    const parsed = parseColId(col.colId);
                    if (parsed.type !== 'hedge' || !parsed.hedgeId) {
                        expandedState.push(col);
                        return;
                    }

                    const hedgeName = this._acquiredHedgeIdMap.get(parsed.hedgeId);
                    if (!hedgeName) {
                        expandedState.push(col);
                        return;
                    }

                    const newHedgeId = nameToNewId.get(hedgeName);
                    if (!newHedgeId || newHedgeId === parsed.hedgeId) {
                        expandedState.push(col);
                        return;
                    }

                    const newColId = buildHedgeColId(parsed.silo, newHedgeId, parsed.hedgeSubType);
                    expandedState.push({ ...col, colId: newColId });

                    // New hedge was previously NEW (only had :qty). Now it's EXISTING
                    // with :trans and :outcome. Inject them right after :qty so the group stays together.
                    if (parsed.hedgeSubType === 'qty') {
                        const silo = parsed.silo as string;
                        const transColId = buildHedgeColId(silo, newHedgeId, 'trans');
                        const outcomeColId = buildHedgeColId(silo, newHedgeId, 'outcome');
                        const cols = savedState.colState || [];
                        if (!cols.some(c => parseColId(c.colId).hedgeId === parsed.hedgeId && parseColId(c.colId).hedgeSubType === 'trans')) {
                            expandedState.push({ colId: transColId, hide: true, width: col.width });
                        }
                        if (!cols.some(c => parseColId(c.colId).hedgeId === parsed.hedgeId && parseColId(c.colId).hedgeSubType === 'outcome')) {
                            expandedState.push({ colId: outcomeColId, hide: true, width: col.width });
                        }
                    }
                });

                savedState.colState = expandedState;
                this._acquiredHedgeIdMap.clear();
            }

            if (isVoidObject(savedState)) {
                // 2b. No saved state — use default order from column defs
                savedState.colState = defaultColState;
            } else if (savedState.colState) {
                // 2c. Saved state exists — remove columns no longer in column defs
                savedState.colState = savedState.colState.filter(c => validColIds.has(c.colId));
                // 2d. CR-20260924-002 (owner smoke round 2): a NEW non-base section — an ES silo an
                // ORION preview just created — lands AFTER the base area, where the manual add puts
                // it, not at the front. ag-Grid 24's setColumnState sorts every column the saved
                // order does not know to index -1, i.e. the front of the unpinned area; that is where
                // the ES strike, hedge and total columns were landing, ahead of the SPX hedges.
                savedState.colState = this.placeNewSiloColumns(savedState.colState, defaultColState);
            }

            // 3. Apply column defs to grid (creates column model)
            this.theGrid.api.setColumnDefs(columnDefs);

            // 4. Restore grid state (applies colState, groupState, expirationPnlNormalized)
            this.restoreGridStateProperties(savedState);

            // 5. Modification columns follow the hedge, not the restored state
            this.syncModificationColumns();

            // 6. Apply pending column insertion
            this.applyPendingColumnInsertion(savedState);

            // 7. Save resolved state
            this.saveGridState();
        } finally {
            this._isRestoringGridState = false;
        }
    }

    // CR-20260924-002: every column of a NON-BASE silo (an ES section) that the saved order does not
    // hold yet is inserted right after its nearest predecessor in DEFINITION order that the saved
    // order does hold — the definition puts an ES section after the base totals, so the section
    // lands after the SPX area and its columns stay together, in definition order. Base-silo
    // columns are deliberately left to ag-Grid's historic placement (a new SPX preview column has
    // always appeared at the front, and nothing here changes that). Only the non-base rule is new.
    private placeNewSiloColumns(saved: ColumnState[], defaults: ColumnState[]): ColumnState[] {
        const base = this.hedgeMatrixDataService ? this.hedgeMatrixDataService.getPortfolioUnderlying() : null;
        const out = saved.slice();
        const known = new Set<string>(out.map(c => c.colId));
        for (let i = 0; i < defaults.length; i++) {
            const def = defaults[i];
            if (known.has(def.colId)) {
                continue;
            }
            const silo = parseColId(def.colId).silo;
            if (!silo || silo === base) {
                continue;   // not an ES-section column: ag-Grid places it as it always has
            }
            let insertAt = -1;
            for (let j = i - 1; j >= 0 && insertAt < 0; j--) {
                const idx = out.findIndex(c => c.colId === defaults[j].colId);
                if (idx >= 0) {
                    insertAt = idx + 1;
                }
            }
            if (insertAt < 0) {
                const firstUnpinned = out.findIndex(c => !c.pinned);
                insertAt = firstUnpinned < 0 ? out.length : firstUnpinned;
            }
            out.splice(insertAt, 0, def);
            known.add(def.colId);
        }
        return out;
    }

    private flattenColumnDefs(defs: any[], out: ColumnState[]) {
        for (const def of defs) {
            if (def.children) {
                this.flattenColumnDefs(def.children, out);
            } else {
                out.push({
                    colId: def.colId || def.field,
                    pinned: def.pinned || null,
                    hide: def.hide || false,
                    width: def.width,
                });
            }
        }
    }

    // A hedge under modification SHOWS its Trans/Outcome columns; one that is not, hides them. The
    // hedge is the single source of truth, re-derived on every applyColumnDefs.
    //
    // This used to only ever HIDE — it returned early for a hedge under modification and never
    // showed one — which made the invariant one-way and cost us CR-20260724-001's attribution bug:
    // an Adjust Preview folded a trade into hedge "111", the outcome reached the model (the P&L and
    // the totals carried it), and then the rebuild that follows hid the two columns that display
    // it, so the trader saw a hedge with its held quantities and no visible change at all.
    //
    // Fixing it at that ONE call site would have left the same hole on every other rebuild — a
    // strike-count change, a paste, removing a previewed hedge, un-Previewing a second Solution —
    // and on the Close tab, which reveals these same columns and equally never re-asserts them.
    // previewCopilotClose has always looked correct only because it creates nothing and therefore
    // never rebuilds.
    //
    // Safe to SHOW here because visibility and modification state are already coupled: modifyHege
    // ends the modification when these columns go hidden and starts one when they are shown, so a
    // hedge that is under modification with its columns collapsed is not a state a trader can reach
    // and ask to keep. A new hedge is untouched — getNewHedgeColumn emits a :qty column only.
    private syncModificationColumns() {
        const allCols = this.theGrid.columnApi.getAllColumns() || [];
        allCols.forEach(col => {
            const colId = col.getColId();
            if (!isHedgeColumn(colId)) return;
            const parsed = parseColId(colId);
            if (parsed.hedgeSubType !== 'trans' && parsed.hedgeSubType !== 'outcome') return;
            const hedge = this.hedgeMatrixDataService.getHedge(parsed.hedgeId);
            this.theGrid.columnApi.setColumnVisible(col, !!hedge?.underModification);
        });
    }

    private applyPendingColumnInsertion(savedState: GridState | undefined) {
        const targetColId = this._pendingInsertAtColId;
        const insertSide = this._pendingInsertSide;
        if (targetColId) {
            console.log('[applyPendingColumnInsertion] CONSUMING pendingInsert:', targetColId, insertSide);
            console.trace('[applyPendingColumnInsertion] call stack');
        }
        this._pendingInsertAtColId = null;
        this._pendingInsertSide = 'left';

        if (!targetColId || !this.theGrid) return;

        const currentState = this.theGrid.columnApi.getColumnState();
        const savedColIds = new Set(savedState?.colState?.map(c => c.colId) || []);

        // New columns = in current state but not in saved state
        const newEntries = currentState.filter(c => !savedColIds.has(c.colId));
        if (newEntries.length === 0) return;

        // Remove new entries from current positions
        const withoutNew = currentState.filter(c => savedColIds.has(c.colId));

        // Resolve hedge group boundary
        const resolvedTarget = this.resolveGroupBoundary(targetColId, insertSide, withoutNew);

        // Find target position — if target is pinned, insert at first unpinned position
        let targetIdx = withoutNew.findIndex(c => c.colId === resolvedTarget);
        const targetIsPinned = targetIdx >= 0 && withoutNew[targetIdx].pinned;
        if (targetIsPinned) {
            targetIdx = withoutNew.findIndex(c => !c.pinned);
        }

        if (targetIdx < 0) {
            withoutNew.push(...newEntries);
        } else {
            const insertIdx = targetIsPinned ? targetIdx : (insertSide === 'left' ? targetIdx : targetIdx + 1);
            withoutNew.splice(insertIdx, 0, ...newEntries);
        }

        this.theGrid.columnApi.setColumnState(withoutNew);
    }

    private loadSavedGridState(): GridState | undefined {
        const pfKey = makePortfolioKey(this.selectedPortfolio);
        const rawState = this._userSettingsService.getValue<GridState>(`hg.matrix.columns.${pfKey}`);
        if (isVoid(rawState)) return undefined;

        // Migrate old-format column IDs to new format
        const baseSilo = this.hedgeMatrixDataService?.getPortfolioUnderlying();
        if (!baseSilo) return rawState;

        const esUnderlyings = this.hedgeMatrixDataService.getActiveNonBaseUnderlyings
            ? this.hedgeMatrixDataService.getActiveNonBaseUnderlyings()
            : [];
        const resolveHedgeSilo = (hedgeId: string) => {
            const h = this.hedgeMatrixDataService.getHedge(hedgeId);
            return h?.underlying || baseSilo;
        };
        return migrateGridState(rawState, resolveHedgeSilo, baseSilo, esUnderlyings) as GridState;
    }

    private restoreGridStateProperties(state: GridState | undefined) {
        if (!state || !this.theGrid) return;

        if (state.expirationPnlNormalizedState) {
            this.expirationPnlShowNormalized = state.expirationPnlNormalizedState;
        }

        if (state.colState) {
            this.theGrid.columnApi.setColumnState(state.colState);
        }

        if (state.groupState) {
            this.theGrid.columnApi.setColumnGroupState(state.groupState);
        }
    }




    isPnlExpirationSelected(expiration: any, allInclusive: boolean): boolean {
        const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const id = buildPnlExpirationColId(baseSilo, expiration.expiration, expiration.side, allInclusive);
        const col = this.theGrid.columnApi.getColumn(id);
        return !isVoid(col) && col.isVisible();
    }

    toggleSelectAllPnlExpirations(args: DxValueChanged<boolean>) {
        if (!args.event) {
            return;
        }

        this.pnlExpirationsList
            .forEach(x => this.onPnlExpirationSelectionChanged(x, args.value));
    }

    recalculatePnls() {
        const strikesToShow = this.getStrikesToShow();

        this.hedgeMatrixDataService.calculatePnls(
            strikesToShow
        );

        this.theGrid.api.refreshCells();

        // The single post-mutation choke (25+ callers). Notify the CoPilot child so its OnPush stale /
        // Modified indicators re-check on a REAL matrix change instead of polling the quote tick.
        this._messageBus.publishAsync({ topic: 'Hg.HedgeMatrixChanged', payload: {} });
    }

    onGrandTotalExpirationSelectionChanged(expiration: any, silo?: string) {
        const resolvedSilo = silo || this.hedgeMatrixDataService.getPortfolioUnderlying();
        const id = buildPnlTotalColId(resolvedSilo, expiration.expiration);

        const column = this.theGrid.columnApi.getColumn(id);

        if (isVoid(column)) {
            return;
        }

        const visible = !column.isVisible();
        this.theGrid.columnApi.setColumnVisible(column, visible);

        if (visible) {
            this.recalculatePnls();
        }

        this.theGrid.api.refreshCells({force: true});
    }

    isGrandTotalExpirationSelected(expiration: any) {
        const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const id = buildPnlTotalColId(baseSilo, expiration.expiration);
        const col = this.theGrid.columnApi.getColumn(id);
        return !isVoid(col) && col.isVisible();
    }

    @DetectMethodChanges()
    decreaseZoom() {
        let desiredZoom = this.zoomLevel -= 0.1;
        if (desiredZoom < 0.1) {
            desiredZoom = 0.1;
        }
        this.setZoomInternal(desiredZoom);
    }

    @DetectMethodChanges()
    increaseZoom() {
        let desiredZoom = this.zoomLevel += 0.1;
        this.setZoomInternal(desiredZoom);
    }

    @DetectMethodChanges()
    setZoom($event: DxValueChanged<number>) {

        if (isVoid($event.event)) {
            return;
        }

        let zoom = $event.value / 100;

        this.setZoomInternal(zoom);
    }

    getVisibleHedges(): string[] {
        const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const visibleHedges = this.hedgeMatrixDataService.getHedges()
            .filter(hedge => {
                const silo = hedge.underlying || baseSilo;
                const colId = buildHedgeColId(silo, hedge.id, 'qty');
                const isVisible = this.theGrid.columnApi.getColumn(colId)?.isVisible();
                return isVisible;
            })
            .map(x => x.id);

        return visibleHedges;
    }

    @DetectMethodChanges()
    onHeadersModeChanged() {
        if (this.headers === 'Hide') {
            this.theGrid.api.setPinnedTopRowData([]);
        } else if (this.headers === 'All') {
            const pinnedRows = this.getPinnedRows();
            this.theGrid.api.setPinnedTopRowData(pinnedRows);
        } else if (this.headers === 'Price Only') {
            const pinnedRows = this.getPinnedRows()
                .filter(x => x.isHedgePrice);
            this.theGrid.api.setPinnedTopRowData(pinnedRows);
        }
    }

    getAtmStrike(): number {
        const portfolioUnderlying = this.hedgeMatrixDataService.getPortfolioUnderlying();
        if (isVoid(portfolioUnderlying)) {
            return undefined;
        }
        const atm = this._atmStrikeService.getCurrentAtm(portfolioUnderlying);
        return atm;
    }

    @DetectMethodChanges({isAsync: true})
    async onNewHedgeExpirationChanged(hedgeId: string, expiration: string) {

        // CR-20260901-005: whole-hedge roll via the expiration editor = one transaction. The edit
        // session's pending BEGIN (taken at cellEditingStarted, before the editor touched
        // initialExpiration) is kept by ensure; END after the roll completes below.
        this.ensureMatrixUndoTransaction();

        await this.hedgeMatrixDataService.onNewHedgeExpirationChanged(
            hedgeId,
            expiration
        );

        this.fillReferenceLists();
        const columnDefs = getColumnDefs(this);
        this.applyColumnDefs(columnDefs);

        this.endMatrixUndoTransaction();
    }

    private addHedgeColumns(hedge: HedgeData) {
        console.log('[addHedgeColumns] START. pendingInsert:', this._pendingInsertAtColId, this._pendingInsertSide);
        this.fillReferenceLists();
        const newColDef = getHedgeColumn(this, hedge);

        // Read current defs from grid, sanitize, append new hedge group
        const currentDefs = this.theGrid.api.getColumnDefs();
        currentDefs.forEach(colDef => {
            Object.keys(colDef).forEach(key => {
                if (colDef[key] === null) delete colDef[key];
            });
            const group = colDef as any;
            if (group.children) {
                group.children.forEach(child => {
                    Object.keys(child).forEach(key => {
                        if (child[key] === null) delete child[key];
                    });
                });
            }
        });

        // If first hedge in a non-base silo, also add strike + total qty columns
        // Order: Strike → Hedge → Total Calls → Total Puts
        const hedgeUl = hedge.underlying || this.hedgeMatrixDataService.getPortfolioUnderlying();
        const baseUl = this.hedgeMatrixDataService.getPortfolioUnderlying();
        const section = (hedgeUl !== baseUl)
            ? this.nonBaseUnderlyingSections.find(s => s.underlying === hedgeUl)
            : null;

        if (section && this.hedgeMatrixDataService.getHedgesByUnderlying(hedgeUl).length === 1) {
            const strikeColId = buildStrikeColId(hedgeUl);
            if (!this.theGrid.columnApi.getColumn(strikeColId)) {
                currentDefs.push(buildEsStrikeGroup(this, section));
            }
            currentDefs.push(newColDef);
            const ulDisplay = section.displayName;
            const totalCalls = getEsQtyTotalColumn(this, 'Call', section, ulDisplay);
            const totalPuts = getEsQtyTotalColumn(this, 'Put', section, ulDisplay);
            if (totalCalls) currentDefs.push(totalCalls);
            if (totalPuts) currentDefs.push(totalPuts);
        } else {
            currentDefs.push(newColDef);
        }

        // Pricing / P&L / Grand-Total columns for the expirations this hedge brought in.
        // Only the FIRST hedge in the grid gets them through the full rebuild; every later hedge
        // (a new SPX expiration, or the first ES hedge on top of existing SPX hedges) came through
        // this incremental path and never got them — the column dialog then listed P&L rows whose
        // column did not exist, and the toggle silently did nothing.
        const leafColIds = (defs: any[]): string[] => defs.flatMap(d =>
            d.children ? leafColIds(d.children) : (d.colId ? [d.colId] : []));
        const existingColIds = new Set(leafColIds(currentDefs));
        const missingExpirationDefs = getColumnDefs(this).filter(def => {
            const ids = leafColIds([def]);
            if (ids.length === 0 || ids.every(id => existingColIds.has(id))) {
                return false;
            }
            const type = parseColId(ids[0]).type;
            return type === 'pricing' || type === 'pnl:expiration' || type === 'pnl:total';
        });
        currentDefs.push(...missingExpirationDefs);
        const missingExpirationColIds = leafColIds(missingExpirationDefs);

        // Capture current column state before setColumnDefs (which resets display order)
        const prevColState = this.theGrid.columnApi.getColumnState();

        this._isRestoringGridState = true;
        try {
            this.theGrid.api.setColumnDefs(currentDefs);

            // Build merged state: previous columns in their order + new columns inserted at target
            const hedgeSilo = hedge.underlying || baseUl;
            const newColIds = new Set<string>(missingExpirationColIds);

            // Silo columns (strike, total qty) if first hedge in silo
            if (section && this.hedgeMatrixDataService.getHedgesByUnderlying(hedgeUl).length === 1) {
                newColIds.add(buildStrikeColId(hedgeUl));
                newColIds.add(buildQtyTotalColId(hedgeUl, 'Call'));
                newColIds.add(buildQtyTotalColId(hedgeUl, 'Put'));
            }

            // Hedge columns
            newColIds.add(buildHedgeColId(hedgeSilo, hedge.id, 'qty'));
            newColIds.add(buildHedgeColId(hedgeSilo, hedge.id, 'trans'));
            newColIds.add(buildHedgeColId(hedgeSilo, hedge.id, 'outcome'));

            // Get full state after setColumnDefs (includes new columns)
            const fullState = this.theGrid.columnApi.getColumnState();
            const newEntries = fullState.filter(c => newColIds.has(c.colId));

            // Start with previous order (excludes new columns)
            const merged = prevColState.slice();

            if (this._pendingInsertAtColId && newEntries.length > 0) {
                const resolvedTarget = this.resolveGroupBoundary(
                    this._pendingInsertAtColId as string, this._pendingInsertSide, merged
                );
                let targetIdx = merged.findIndex(c => c.colId === resolvedTarget);
                const targetIsPinned = targetIdx >= 0 && merged[targetIdx].pinned;
                if (targetIsPinned) {
                    targetIdx = merged.findIndex(c => !c.pinned);
                }
                if (targetIdx < 0) {
                    merged.push(...newEntries);
                } else {
                    const insertIdx = targetIsPinned
                        ? targetIdx
                        : (this._pendingInsertSide === 'left' ? targetIdx : targetIdx + 1);
                    merged.splice(insertIdx, 0, ...newEntries);
                }
                this._pendingInsertAtColId = null as any;
                this._pendingInsertSide = 'left';
            } else {
                // No target — append at end
                merged.push(...newEntries);
            }

            this.theGrid.columnApi.setColumnState(merged);
        } finally {
            this._isRestoringGridState = false;
        }

        this.recalculatePnls();
        this.theGrid.api.refreshCells({ force: true });
    }

    @DetectMethodChanges()
    rebuildColumns(skipStateRestore = false) {
        this.fillReferenceLists();
        const columnDefs = getColumnDefs(this);
        if (skipStateRestore) {
            this._isRestoringGridState = true;
            try {
                this.theGrid.api.setColumnDefs(columnDefs);
            } finally {
                this._isRestoringGridState = false;
            }
        } else {
            this.applyColumnDefs(columnDefs);
        }
        this.recalculatePnls();
        this.theGrid.api.refreshCells({ force: true });
    }

    rebuildRows() {

        const firstDisplayedRow = this.theGrid.api.getFirstDisplayedRow();
        const lastDisplayedRow = this.theGrid.api.getLastDisplayedRow();

        const middle = Math.ceil(((lastDisplayedRow - firstDisplayedRow) / 2) + firstDisplayedRow);

        this.fillReferenceLists();

        this.theGrid.api.setRowData([]);

        const columnDefs = getColumnDefs(this);

        this.applyColumnDefs(columnDefs);

        const rows = this.getRows();

        this.hedgeMatrixDataService.calculatePnls(
            rows.map(x => x.strike)
        );

        this.theGrid.api.setRowData(rows);

        const pinned = this.getPinnedRows();

        this.theGrid.api.setPinnedTopRowData(pinned);

        this.theGrid.api.refreshCells({force: true});

        const theNode = this.theGrid.api.getDisplayedRowAtIndex(middle);

        if (!isVoid(theNode)) {
            this.theGrid.api.ensureNodeVisible(nd => nd === theNode, 'middle');
        }
    }

    addSpacer(insertAtColId?: string, insertSide: 'left' | 'right' = 'left') {
        for (let i = 1; i <= 7; i++) {
            const colId = buildSpacerColId(i);
            const spacerCol = this.theGrid.columnApi.getColumn(colId);
            console.assert(!!spacerCol);
            if (!spacerCol.isVisible()) {
                this.theGrid.columnApi.setColumnVisible(spacerCol, true);
                if (insertAtColId) {
                    this.moveColumn(colId, insertAtColId, insertSide);
                }
                break;
            }
        }
    }

    /**
     * If targetColId is inside a hedge group (qty/trans/outcome), resolve to
     * the group boundary: first member for 'left', last member for 'right'.
     * Non-hedge columns pass through unchanged.
     */
    private resolveGroupBoundary(targetColId: string, side: 'left' | 'right', stateArray: any[]): string {
        if (!isHedgeColumn(targetColId)) return targetColId;
        const parsed = parseColId(targetColId);
        if (!parsed.hedgeId || !parsed.silo) return targetColId;

        const qtyId = buildHedgeColId(parsed.silo, parsed.hedgeId, 'qty');
        const transId = buildHedgeColId(parsed.silo, parsed.hedgeId, 'trans');
        const outcomeId = buildHedgeColId(parsed.silo, parsed.hedgeId, 'outcome');
        const groupMembers = [qtyId, transId, outcomeId];

        const memberIndices = groupMembers
            .map(id => stateArray.findIndex(c => c.colId === id))
            .filter(idx => idx >= 0);

        if (memberIndices.length === 0) return targetColId;

        if (side === 'left') {
            return stateArray[Math.min(...memberIndices)].colId;
        } else {
            return stateArray[Math.max(...memberIndices)].colId;
        }
    }

    showAndMoveColumn(colIdToShow: string, targetColId: string, side: 'left' | 'right' = 'left') {
        const state = this.theGrid.columnApi.getColumnState();
        const entry = state.find(c => c.colId === colIdToShow);
        if (!entry) {
            console.warn('[showAndMoveColumn] column not found in state:', colIdToShow);
            return;
        }

        entry.hide = false;

        const resolvedTarget = this.resolveGroupBoundary(targetColId, side, state);
        const filtered = state.filter(c => c.colId !== colIdToShow);
        let targetIdx = filtered.findIndex(c => c.colId === resolvedTarget);
        const targetIsPinned = targetIdx >= 0 && filtered[targetIdx].pinned;
        if (targetIsPinned) {
            targetIdx = filtered.findIndex(c => !c.pinned);
        }

        console.log('[showAndMoveColumn]', {
            colIdToShow,
            targetColId,
            resolvedTarget,
            side,
            targetIdx,
            targetIsPinned,
            filteredLength: filtered.length,
        });

        if (targetIdx < 0) {
            filtered.push(entry);
        } else {
            const insertIdx = targetIsPinned ? targetIdx : (side === 'left' ? targetIdx : targetIdx + 1);
            filtered.splice(insertIdx, 0, entry);
        }

        this.theGrid.columnApi.setColumnState(filtered);
    }

    moveColumn(colIdToMove: string, targetColId: string, side: 'left' | 'right' = 'left') {
        const state = this.theGrid.columnApi.getColumnState();

        // Resolve hedge group boundary
        const resolvedTarget = this.resolveGroupBoundary(targetColId, side, state);

        // Remove the column to move
        const filtered = state.filter(c => c.colId !== colIdToMove);
        const entry = state.find(c => c.colId === colIdToMove);
        if (!entry) {
            console.warn('[moveColumn] ENTRY NOT FOUND:', colIdToMove, '| available colIds:', state.map(c => c.colId));
            return;
        }

        // Find target — if pinned, insert at first unpinned position (ignore side)
        let targetIdx = filtered.findIndex(c => c.colId === resolvedTarget);
        const targetIsPinned = targetIdx >= 0 && filtered[targetIdx].pinned;
        if (targetIsPinned) {
            targetIdx = filtered.findIndex(c => !c.pinned);
        }

        console.log('[moveColumn]', { colIdToMove, targetColId, resolvedTarget, side, targetIdx, targetIsPinned, entryFound: !!entry });

        if (targetIdx < 0) {
            console.warn('[moveColumn] TARGET NOT FOUND:', resolvedTarget);
            filtered.push(entry);
        } else {
            const insertIdx = targetIsPinned ? targetIdx : (side === 'left' ? targetIdx : targetIdx + 1);
            filtered.splice(insertIdx, 0, entry);
        }

        this.theGrid.columnApi.setColumnState(filtered);
    }

    removeSpacer(colId: string) {
        this.theGrid.columnApi.setColumnVisible(colId, false);
    }

    @DetectMethodChanges({isAsync: true})
    async rebuildGrid() {
        await this.onPortfolioSelected(this.selectedPortfolio);

        // Rebuild reloads from the saved state and exits modification mode on every hedge (see
        // onPortfolioSelected) — the trader's in-flight modifications are gone, so the CoPilot resets
        // (owner 2026-08-03).
        //
        // This publish is also the ONLY notification the CoPilot gets from a rebuild: onPortfolioSelected
        // calls hedgeMatrixDataService.calculatePnls() directly instead of this component's
        // recalculatePnls(), so it bypasses the post-mutation choke and never publishes
        // Hg.HedgeMatrixChanged. Do not "simplify" this away on the assumption the matrix already
        // announced the change — it did not.
        this._messageBus.publish<HedgeBookResetPayload>({
            topic: HEDGE_BOOK_RESET_TOPIC,
            payload: {reason: 'grid-rebuilt'}
        });
    }

    onNormalizedExpirationPnlToggled(value: boolean, item: Partial<ExpirationDescriptor>) {
        if (value) {
            if (!this.expirationPnlShowNormalized.includes(item.expiration)) {
                this.expirationPnlShowNormalized.push(item.expiration);
            }
        } else {
            const ix = this.expirationPnlShowNormalized.indexOf(item.expiration);
            if (ix >= 0) {
                this.expirationPnlShowNormalized.splice(ix, 1);
            }
        }
        this.saveGridState();
        this.theGrid?.api.refreshCells();
    }

    isNormalizedExpirationPnlEnabled(item: Partial<ExpirationDescriptor>) {
        return this.expirationPnlShowNormalized.includes(item.expiration);
    }

    @DetectMethodChanges()
    showSlideHedgeDialog(hedgeData: HedgeData, column: 'trans' | 'outcome') {
        const cb = (direction: 'Up' | 'Down') => {
            this.slideHedge(hedgeData, direction, column);
        };

        this.slideHedgeDialog.show(cb);
    }

    isHedgeExpired(hedgeData: HedgeData): boolean {

        if (isVoid(hedgeData)) {
            return false;
        }

        if (isVoid(hedgeData.legs)) {
            return false;
        }

        return hedgeData.legs.some(x => this._optionsChainService.isOptionExpired(x.ticker));
    }

    @DetectMethodChanges()
    showPocket() {
        this.hedgePocket.show({portfolio: this.selectedPortfolio});
    }

    private slideHedge(hedgeData: HedgeData, direction: 'Up' | 'Down', column: 'trans' | 'outcome') {

        // CR-20260901-005: one slide = one transaction (BEGIN before the async mutation runs —
        // nothing else can mutate under the loading overlay; END in the finally below).
        this.ensureMatrixUndoTransaction();

        this.theGrid.api.showLoadingOverlay();

        const strikesToShow = this.getStrikesToShow();

        setTimeout(() => {
            const newStrikes: number[] = [];
            try {

                const cellDataForHedge = this.hedgeMatrixDataService
                    .getCellDataForHedge(hedgeData.id);

                const multiplier = direction === 'Up' ? 1 : -1;

                const sortedCellData = cellDataForHedge
                    .slice()
                    .sort((a, b) => (b.strike - a.strike) * multiplier);

                for (let cd of sortedCellData) {

                    const shiftedStrike = cd.strike + this.strikeStep * multiplier;

                    const needToAddRow = strikesToShow.findIndex(strike => strike === shiftedStrike) === -1;

                    console.log(`Sliding "${hedgeData.name}" strike ${cd.strike} to ${shiftedStrike}`);

                    if (column === 'outcome') {
                        const qty = cd.outcomeQty;

                        this.hedgeMatrixDataService.setOutcomeQty(
                            hedgeData,
                            cd.strike,
                            cd.expiration,
                            null
                        );

                        this.hedgeMatrixDataService.setOutcomeQty(
                            hedgeData,
                            shiftedStrike,
                            cd.expiration,
                            qty
                        );
                    } else if (column === 'trans') {

                        this.hedgeMatrixDataService.removeHedgeCellData(cd);

                        this.hedgeMatrixDataService.setTransQty(
                            hedgeData,
                            shiftedStrike,
                            cd.expiration,
                            cd.transQty
                        );
                    }

                    if (needToAddRow) {
                        const otm = shiftedStrike - this.centerStrike;

                        this.theGrid.api.applyTransactionAsync({
                            add: [
                                {
                                    rowId: shiftedStrike + '',
                                    otm,
                                    strike: shiftedStrike,
                                    rowSpan: 1,
                                } as HedgeMatrixRow
                            ]
                        });

                        newStrikes.push(shiftedStrike);
                    }
                }

            } finally {
                const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();
                this.theGrid.api.setSortModel([
                    { colId: buildStrikeColId(baseSilo), sort: 'desc' }
                ]);

                // Subscribe pricing tickers for newly added strikes
                if (newStrikes.length > 0) {
                    const visiblePricingCols = this.theGrid.columnApi.getAllDisplayedColumns()
                        .filter(c => c.isVisible() && isPricingColumn(c.getColId()));

                    visiblePricingCols.forEach(col => {
                        const parsed = parseColId(col.getColId());
                        newStrikes.forEach(strike => {
                            const ticker = this.hedgeMatrixDataService.getTickerForCell(
                                strike, parsed.exp, parsed.side as any, parsed.silo !== baseSilo ? parsed.silo : undefined
                            );
                            if (ticker) {
                                this.lastQuoteCache.subscribeIfNotYet(ticker);
                            }
                        });
                    });
                }

                this.recalculatePnls();
                this.theGrid.api.hideOverlay();
                this.theGrid.api.refreshCells();

                // CR-20260901-005: END — the slide's mutations are complete (even on a throw,
                // whatever mutated commits as one step rather than leaking into the next gesture).
                this.endMatrixUndoTransaction();
            }
        });
    }

    private setZoomInternal(zoom: number) {
        if (!isValidNumber(zoom)) {
            return;
        }

        if (zoom < 0.1) {
            zoom = 0.1;
        }

        this.zoomLevel = zoom;
    }

    rollOtherLegs(expiration: string, otherLegs: OtherLegWrapper[], hedge: HedgeData) {
        otherLegs.forEach(leg => {

            this.hedgeMatrixDataService.rollLeg(
                hedge,
                leg.strike,
                expiration,
                leg.qty
            );
        });
    }

    amendOtherLegs(expiration: string, otherLegs: OtherLegWrapper[], hedge: HedgeData) {
        otherLegs.forEach(leg => {

            this.hedgeMatrixDataService.setTransQty(
                hedge,
                leg.strike,
                leg.expiration,
                null
            );

            this.hedgeMatrixDataService.setTransQty(
                hedge,
                leg.strike,
                expiration,
                leg.qty
            );
        });
    }

    toggleHedgeModifications(hedge: any, onOff: boolean) {

        // CR-20260901-005: both directions mutate trans quantities (stash/restore) — one transaction.
        this.ensureMatrixUndoTransaction();

        this.isLoading = true;

        try {

            const hedgeData = this.hedgeMatrixDataService
                .getHedge(hedge.id);

            if (onOff) {
                const copy = hedgeData['modifications'] as HedgeMatrixCellData[];

                if (!isVoid(copy)) {
                    this.hedgeMatrixDataService.setCellData(copy);
                }

            } else {

                const cellDataForHedge = this.hedgeMatrixDataService
                    .getCellDataForHedge(hedge.id);

                const copy = jsonCloneObject(cellDataForHedge);

                cellDataForHedge.forEach(cd => {
                    this.hedgeMatrixDataService.setTransQty(
                        hedgeData,
                        cd.strike,
                        cd.expiration,
                        null
                    );

                    hedgeData['modifications'] = copy;
                });

            }

        } finally {
            // CR-20260901-005: END — the toggle's synchronous mutations are done here (the
            // timeout below only redraws); a throw still commits what mutated as one step.
            this.endMatrixUndoTransaction();

            setTimeout(() => {
                this.theGrid.api.redrawRows();
                this.isLoading = false
            }, 250);
        }
    }

    private restoreSubscriptionsOfExpirationColumns() {
        const baseSilo = this.hedgeMatrixDataService.getPortfolioUnderlying();

        const expirationColumns = this.theGrid.columnApi.getAllDisplayedColumns()
            .filter(clmn => clmn.isVisible())
            .filter(clmn => isPricingColumn(clmn.getColId()));

        expirationColumns.forEach(expCol => {
            const parsed = parseColId(expCol.getColId());
            const silo = parsed.silo;
            const expiration = parsed.exp;
            const side = parsed.side as any;
            const isEs = silo !== baseSilo;

            if (!isEs) {
                this.theGrid.api.forEachNode(node => {
                    const strike: number = node.data.strike;
                    if (!isValidNumber(strike, true)) {
                        return;
                    }
                    const ticker = this.hedgeMatrixDataService.getTickerForCell(
                        strike, expiration, side
                    );
                    this.lastQuoteCache.subscribeTicker(ticker);
                });
            } else {
                const fairValue = this.hedgeMatrixDataService.getFairValueOffset(silo);

                this.theGrid.api.forEachNode(node => {
                    const strike: number = node.data.strike;
                    if (!isValidNumber(strike, true)) {
                        return;
                    }
                    const esStrike = strike + fairValue;
                    const ticker = this.hedgeMatrixDataService.getTickerForCell(
                        esStrike, expiration, side, silo
                    );
                    if (!ticker) { return; }
                    this.lastQuoteCache.subscribeTicker(ticker);
                });
            }
        });
    }

    get canPasteHedgeFromOpp(): boolean {
        return this._clipboardService.hasKey('hg.opp-data');
    }

    get canPasteHedgeFromOpg(): boolean {
        return this._clipboardService.hasKey('hg.opg-data');
    }

    get canPasteHedgeFromPcg(): boolean {
        return this._clipboardService.hasKey('hg.pkg-cmprsn');
    }

    get canPasteHedgeFromClipboard(): boolean {
        return this._clipboardService.hasKey(ETS_CLIPBOARD_LEGS_KEY);
    }

    @DetectMethodChanges({isAsync: true})
    async pasteHedgeFromClipboard() {
        const universal: ComboHighlightedUIMessage | ComboHighlightedUIMessage[] =
            this._clipboardService.get(ETS_CLIPBOARD_LEGS_KEY);

        if (isVoid(universal)) {
            this._toastr.error('Cannot paste hedge: clipboard is empty');
            return;
        }

        const messages = Array.isArray(universal) ? universal : [universal];

        // FR-21 cross-underlying check.
        const targetUnderlying = this._hedgeMatrixDataService.getPortfolioUnderlying();
        for (const m of messages) {
            const sourceUl = m?.items?.[0]?.underlying;
            if (sourceUl && targetUnderlying && sourceUl !== targetUnderlying) {
                const sourceDisplay = this._tiService.getInstrumentByTicker(sourceUl)?.displayName ?? sourceUl;
                const targetDisplay = this._tiService.getInstrumentByTicker(targetUnderlying)?.displayName ?? targetUnderlying;
                this._toastr.error(`Ticker mismatch: clipboard=${sourceDisplay} target=${targetDisplay}. Paste refused.`);
                console.log('[ETS-CLIPBOARD] paste rejected target=hm reason=ticker-mismatch');
                return;
            }
        }

        // FR-6: split each message by Call/Put. Mixed-type message becomes two
        // separate hedges (one per side); single-type passes through verbatim.
        const splitMessages: ComboHighlightedUIMessage[] = [];
        for (const m of messages) {
            if (!m?.items?.length) continue;
            const callItems = m.items.filter(i => parseOptionTicker(i.ticker)?.type === 'Call');
            const putItems = m.items.filter(i => parseOptionTicker(i.ticker)?.type === 'Put');
            if (callItems.length > 0 && putItems.length > 0) {
                splitMessages.push({ ...m, items: callItems });
                splitMessages.push({ ...m, items: putItems });
            } else {
                splitMessages.push(m);
            }
        }

        let allOk = true;
        for (const message of splitMessages) {
            const ok = await this.pasteFromOpgSingle(message);
            if (!ok) {
                allOk = false;
            }
        }

        this.fillReferenceLists();
        const columnDefs = getColumnDefs(this);
        this.applyColumnDefs(columnDefs);
        this.recalculatePnls();
        this.removeOrphanCompanionRows();

        console.log(`[ETS-CLIPBOARD] read target=hm via=universal-key messages=${messages.length} hedgesAfterSplit=${splitMessages.length} allOk=${allOk}`);

        if (allOk) {
            this._toastr.success('Strategy pasted');
        }
    }

    @DetectMethodChanges({isAsync: true})
    async pasteHedgeFromOpg() {

        const message = this._clipboardService.get('hg.opg-data') as ComboHighlightedUIMessage;

        if (isVoid(message)) {
            this._toastr.error('Cannot paste hedge: clipboard is empty');
            return;
        }

        if (Array.isArray(message)) {
            for (const singleMessage of message) {
                await this.pasteFromOpgSingle(singleMessage);
            }
        } else {
            await this.pasteFromOpgSingle(message);
        }

        this.fillReferenceLists();

        const columnDefs = getColumnDefs(this);

        this.applyColumnDefs(columnDefs);

        this.recalculatePnls();
        this.removeOrphanCompanionRows();
    }

    private async pasteFromOpgSingle(message: ComboHighlightedUIMessage): Promise<boolean> {

        if (isVoid(message.items)) {
            this._toastr.error('Cannot paste hedge: no legs provided');
            return false;
        }

        const items = message.items.map(x => {

            const optionTicker = parseOptionTicker(x.ticker);

            const side = x.side === MarketSide.Buy ? 1 : -1;

            return {
                type: optionTicker.type,
                strike: optionTicker.strike,
                expiration: optionTicker.expiration,
                qty: x.netPosition * side
            }
        });

        if (isVoid(items) || items.length !== message.items.length) {
            this._toastr.error('Cannot paste hedge: bad legs data');
            return false;
        }

        const underlying = this._hedgeMatrixDataService.getPortfolioUnderlying();

        const reconcileEntries = items.map(x => ({ expiration: x.expiration, strike: x.strike }));
        const missing = await this._optionsChainService.getMissingStrikes(underlying, reconcileEntries);

        if (missing.length > 0) {
            this._toastr.warning('Some missing strikes detected. Validating against live quotes…', 'Option Chain', { timeOut: 5000 });
            this.isLoading = true;
            this.theGrid?.api?.showLoadingOverlay();

            let unresolved: { expiration: string; strike: number }[];
            try {
                const r = await this._optionsChainService.tryReconcileMissingStrikes(underlying, missing);
                unresolved = r.unresolved;
            } finally {
                this.isLoading = false;
                this.theGrid?.api?.hideOverlay();
            }

            if (unresolved.length > 0) {
                const list = unresolved
                    .map(x => `${x.strike} @ ${makeGuiFriendlyExpirationDate(x.expiration)}`)
                    .join(', ');
                this._toastr.error(`Cannot paste hedge: strike(s) not tradable on live market — ${list}`);
                return false;
            }
        }

        const side = items[0].type;

        const addedHedge = await this.addHedgeInternal(side, underlying);

        if (isVoid(addedHedge)) {
            this._toastr.error('Cannot paste hedge: failed to add hedge to the matrix');
            return false;
        }

        items.forEach(item => {
            this._hedgeMatrixDataService.setTransQty(
                addedHedge,
                item.strike,
                item.expiration,
                item.qty
            );
        });

        const distinctExpirations = new Set(items.map(i => i.expiration)).size;
        if (distinctExpirations > 2) {
            this._toastr.warning('Provided legs contain more than two expirations. Hedge Matrix column header can correctly visualize only two nearest. Pricing and Order Copy functionality works as expected');
        }

        return true;
    }

    @DetectMethodChanges({isAsync: true})
    async pasteHedgesFromPcg() {

        const messages = this._clipboardService.get('hg.pkg-cmprsn') as ComboHighlightedUIMessage[];

        for (const message of messages) {
            await this.pasteSingleHedgeFromPcg(message);
        }

        this.fillReferenceLists();

        const columnDefs = getColumnDefs(this);

        this.applyColumnDefs(columnDefs);

        this.recalculatePnls();
    }

    @DetectMethodChanges({isAsync: true})
    async pasteHedgeFromOpp() {

        const message = this._clipboardService.get('hg.opp-data') as ComboHighlightedUIMessage;

        if (isVoid(message)) {
            this._toastr.error('Cannot paste hedge: clipboard is empty');
            return;
        }

        if (Array.isArray(message)) {
            for (const singleMessage of message) {
                await this.pasteSingleHedgeFromPcg(singleMessage);
            }
        } else {
            await this.pasteSingleHedgeFromPcg(message);
        }

        this.fillReferenceLists();

        const columnDefs = getColumnDefs(this);

        this.applyColumnDefs(columnDefs);

        this.recalculatePnls();
        this.removeOrphanCompanionRows();
    }

    private async pasteSingleHedgeFromPcg(message: ComboHighlightedUIMessage) {

        if (isVoid(message)) {
            this._toastr.error('Cannot paste hedge: clipboard is empty');
            return;
        }

        if (isVoid(message.items)) {
            this._toastr.error('Cannot paste hedge: no legs provided');
            return;
        }

        const items = message.items.map(x => {

            const optionTicker = parseOptionTicker(x.ticker);

            const side = x.side === MarketSide.Buy ? 1 : -1;

            return {
                type: optionTicker.type,
                strike: optionTicker.strike,
                expiration: optionTicker.expiration,
                qty: x.netPosition * side
            }
        });

        if (isVoid(items) || items.length !== message.items.length) {
            this._toastr.error('Cannot paste hedge: bad legs data');
            return;
        }

        const underlying = this._hedgeMatrixDataService.getPortfolioUnderlying();

        const reconcileEntries = items.map(x => ({ expiration: x.expiration, strike: x.strike }));
        const missing = await this._optionsChainService.getMissingStrikes(underlying, reconcileEntries);

        if (missing.length > 0) {
            this._toastr.warning('Some missing strikes detected. Validating against live quotes…', 'Option Chain', { timeOut: 5000 });
            this.isLoading = true;
            this.theGrid?.api?.showLoadingOverlay();

            let unresolved: { expiration: string; strike: number }[];
            try {
                const r = await this._optionsChainService.tryReconcileMissingStrikes(underlying, missing);
                unresolved = r.unresolved;
            } finally {
                this.isLoading = false;
                this.theGrid?.api?.hideOverlay();
            }

            if (unresolved.length > 0) {
                const list = unresolved
                    .map(x => `${x.strike} @ ${makeGuiFriendlyExpirationDate(x.expiration)}`)
                    .join(', ');
                this._toastr.error(`Cannot paste hedge: strike(s) not tradable on live market — ${list}`);
                return;
            }
        }

        const side = items[0].type;

        const addedHedge = await this.addHedgeInternal(side, underlying);

        if (isVoid(addedHedge)) {
            this._toastr.error('Cannot paste hedge: failed to add hedge to the matrix');
            return;
        }

        items.forEach(item => {
            this._hedgeMatrixDataService.setTransQty(
                addedHedge,
                item.strike,
                item.expiration,
                item.qty
            );
        });

        const distinctExpirations = new Set(items.map(i => i.expiration)).size;
        if (distinctExpirations > 2) {
            this._toastr.warning('Provided legs contain more than two expirations. Hedge Matrix column header can correctly visualize only two nearest. Pricing and Order Copy functionality works as expected');
        }
    }

    private makeComboMessage(hedge: HedgeData): ComboHighlightedUIMessage {
        const comboItems = this.makeComboItems(hedge.id);

        if (isVoid(comboItems)) {
            return;
        }

        const cost = this.hedgeMatrixDataService.getTransCost(hedge?.id);

        const msg: ComboHighlightedUIMessage = {
            items: comboItems,
            orderParams: {
                orderLimitPx: cost,
                orderDuration: TimeInForce.GTC,
                orderQty: 1,
                orderType: OrderType.Limit
            }
        };

        if (!hedge.isNew) {
            msg.hedgingGridTransaction = {
                groupId: hedge?.id
            }
        }

        if (!this._copilotDivergedHedgeIds.has(hedge.id) && this._copilotPreviewedHedgeIds.has(hedge.id)) {
            msg.availableAdjustment = this._availableAdjustmentByHedge[hedge.id];
        }

        return msg;
    }

    getModificationsCount(): number {
        const visibleHedges = this.getVisibleHedges();

        const hedgeWithModifications = visibleHedges
            .filter(hedgeId => {
                const hedge = this._hedgeMatrixDataService.getHedge(hedgeId);
                return hedge.isNew || hedge.underModification
            })
            .map(x => this._hedgeMatrixDataService.getHedge(x))
            .filter(x => {
                const cellData = this._hedgeMatrixDataService.getCellDataForHedge(x.id);
                const transactions = cellData.filter(x => isValidNumber(x.transQty));
                return transactions.length > 0;
            });

        return hedgeWithModifications.length;
    }

    copyAllChangesToHedgingGrid() {

        const visibleHedges = this.getVisibleHedges();

        const visibleNewOrModifiedHedges = visibleHedges.filter(hedgeId => {
            const hedge = this._hedgeMatrixDataService.getHedge(hedgeId);
            return hedge.isNew || hedge.underModification
        }).map(x => this._hedgeMatrixDataService.getHedge(x));


        const configs = visibleNewOrModifiedHedges.map(hedge => {
            const comboItems = this.makeComboItems(hedge.id);

            const orderPx = this.hedgeMatrixDataService.getTransCost(hedge.id) / this.portfolioDefaultQty;

            const config: OrdersVerificationDialogConfig = {
                orderLegs: comboItems.sort((a, b) => b.ticker.localeCompare(a.ticker)),
                orderPrice: orderPx,
                defaultQty: this.portfolioDefaultQty,
                ledgerPrice: () => this.hedgeMatrixDataService.getTransCost(hedge.id),
                groupName: hedge.name,
                groupColor: hedge.color,
            };

            return config;
        });

        // CR-20260827-001: 4th arg opts in to the "Optimize Grouping" preview; the ATM
        // (CR-20260830-002) feeds the backend arrangement's skew rank.
        this.orderVerificationDialog.show(configs, undefined, undefined, true,
            (legs, qty, md) => this.pricingStepsPopup.show(legs, qty, md),
            this.getAtmStrike())
            .then(value => {
                const messages = visibleNewOrModifiedHedges.map(x => this.makeComboMessage(x));
                const clipboardKey = 'hg.opg-data';
                this._clipboardService.put(clipboardKey, messages);
                this._clipboardService.put(ETS_CLIPBOARD_LEGS_KEY, messages);
                console.log(`[ETS-CLIPBOARD] write source=hm via=copyAllChangesToHedgingGrid items=${messages.length} mode=all`);
                this._toastr.success(`All changes copied to clipboard`);
            })
            .catch(reason => {
                //
            });
    }

    getCursorDate(): string {
        return this._shellToday.todayKey(this._hedgeMatrixDataService?.getPortfolioUnderlying()) || undefined;
    }

    recreateGrid() {
        const columnDefs = getColumnDefs(this);
        this.applyColumnDefs(columnDefs);
    }

    async onHedgeHeaderClicked(hedgeId: string) {
        const positionsText = this.extractTextDescription(hedgeId);

        console.log(positionsText);

        try {
            await navigator.clipboard.writeText(positionsText);
            this._toastr.success('Positions data copied to clipboard');
        } catch {
            this._toastr.error('Failed to copy positions data to clipboard');
        }
    }

    private extractTextDescription(hedgeId: string) {
        const hedge = this.hedgeMatrixDataService.getHedge(hedgeId);

        const positionPicks = hedge.legs.map(x => {
            const ticker = x.ticker;
            const qty = x.qty;
            const name = x.label;

            const pick: HedgePositionPick = {
                name,
                qty,
                ticker
            };

            return pick;
        });

        const positionsText = formatHedge(positionPicks);
        return positionsText;
    }

    async onTotalHeaderClicked(expiration: string, side: 'Calls' | 'Puts' | 'Calls & Puts') {

        const hedges = this.hedgeMatrixDataService
            .getHedges()
            .filter(x => !x.isNew && !x.underModification)
            .filter(x => x.legs.some(leg => leg.expiration === expiration))
            .filter(x => {
                if (side === 'Calls & Puts') {
                    return true;
                }

                switch (side) {
                    case "Calls":
                        return x.type === 'Call';
                    case "Puts":
                        return x.type === 'Put';
                    default:
                        return false;
                }
            });

        if (hedges.length === 0) {
            this._toastr.warning(`No '${side}' hedges found`);
            return;
        }

        const descriptions = hedges
            .map(x => this.extractTextDescription(x.id));

        console.log(descriptions);

        const combinedText = descriptions.join('\n\n');

        try {
            await navigator.clipboard.writeText(combinedText);
            this._toastr.success('Positions data copied to clipboard');
        } catch {
            this._toastr.error('Failed to copy positions data to clipboard');
        }
    }

    hasHedgesForExpiration(expiration: string, side: string): number {
        const hasSome = this.hedgeMatrixDataService.getHedges()
            .filter(x => {
                return x.type === side &&
                    !x.isNew &&
                    !x.underModification &&
                    x.legs.some(leg => leg.expiration === expiration);
            });

        return hasSome.length;
    }

    isTextFormatCopyAvailable() {
        return this._accessControlService.isSecureElementAvailable('1cd8ab6a-6fa2-4ef9-98a6-4fdb7b543d38');
    }

    isAcquireChangesAvailable(): boolean {
        return this._accessControlService.isSecureElementAvailable('b583674c-143f-454e-aeee-06bca7d0e2e9');
    }

    private validateHedgeForAcquire(hedge: HedgeData): { valid: boolean, reason?: string } {
        // Guard 1: No transaction legs (empty hedge or no-op modification)
        const cellData = this._hedgeMatrixDataService.getCellDataForHedge(hedge.id);
        const hasTransactions = cellData.some(function(x) { return isValidNumber(x.transQty); });
        if (!hasTransactions) {
            return {valid: false, reason: 'has no changes'};
        }

        // Guard 2: Expired legs
        if (this.isHedgeExpired(hedge)) {
            return {valid: false, reason: 'has expired legs'};
        }

        // Guard 3: Legs with invalid tickers (missing price data)
        const comboItems = this.makeComboItems(hedge.id);
        if (isVoid(comboItems) || comboItems.length === 0) {
            return {valid: false, reason: 'has no valid legs'};
        }
        const hasInvalidTickers = comboItems.some(function(x) { return !x.ticker; });
        if (hasInvalidTickers) {
            return {valid: false, reason: 'has legs with missing ticker/price data'};
        }

        return {valid: true};
    }

    async acquireChanges(hedge: HedgeData) {
        console.log('[Acquire] acquireChanges() called for hedge:', hedge.id, 'name:', hedge.name, 'isNew:', hedge.isNew, 'type:', hedge.type);

        // Validate
        const validation = this.validateHedgeForAcquire(hedge);
        if (!validation.valid) {
            console.log('[Acquire] Validation failed for hedge', hedge.id, ':', validation.reason);
            this._toastr.warning('Cannot acquire "' + hedge.name + '": ' + validation.reason);
            return;
        }

        const comboItems = this.makeComboItems(hedge.id);
        const orderPx = this.hedgeMatrixDataService.getTransCost(hedge.id) / this.portfolioDefaultQty;

        const config: OrdersVerificationDialogConfig = {
            orderLegs: comboItems.sort(function(a, b) { return b.ticker.localeCompare(a.ticker); }),
            orderPrice: orderPx,
            defaultQty: this.portfolioDefaultQty,
            ledgerPrice: () => this.hedgeMatrixDataService.getTransCost(hedge.id),
            isNewHedge: hedge.isNew,
            hedgeName: hedge.isNew ? '' : undefined,
            hedgePlaceholder: hedge.isNew ? hedge.name : undefined,
            groupName: hedge.isNew ? undefined : hedge.name,
            groupColor: hedge.color,
        };

        const existingLabels = await this.getExistingHedgeLabels();
        console.log('[Acquire] Showing order verification dialog for single hedge. existingLabels:', existingLabels);

        // CR-20260827-001: last arg opts in to the "Optimize Grouping" preview (informational
        // here - acquire has no downstream Regroup step; requested by the owner regardless).
        this.orderVerificationDialog.show([config], 'acquire', existingLabels, true,
            (legs, qty, md) => this.pricingStepsPopup.show(legs, qty, md))
            .then(() => {
                console.log('[Acquire] Order verification accepted');

                const msg = this.makeComboMessage(hedge);
                if (isVoid(msg)) {
                    console.log('[Acquire] makeComboMessage returned void — no changes to acquire');
                    this._toastr.warning('No changes to acquire');
                    return;
                }

                msg.strategyName = hedge.isNew ? config.hedgeName : hedge.name;
                console.log('[Acquire] Combo message created. strategyName:', msg.strategyName, 'items count:', msg.items?.length, 'hedgingGridTransaction:', msg.hedgingGridTransaction);

                // Save position mapping before acquire so we can restore after reload
                if (hedge.isNew) {
                    this._acquiredHedgeIdMap.set(hedge.id, msg.strategyName);
                }
                this.saveGridState();

                this.acquireRequested.emit([msg]);
            })
            .catch(() => {
                console.log('[Acquire] Order verification cancelled by user');
            });
    }

    async acquireAllChanges() {
        console.log('[Acquire] acquireAllChanges() called');

        const visibleHedges = this.getVisibleHedges();
        console.log('[Acquire] Visible hedges count:', visibleHedges.length);

        const candidates = visibleHedges
            .map(id => this._hedgeMatrixDataService.getHedge(id))
            .filter(h => h.isNew || h.underModification);

        const skipped: string[] = [];
        const validHedges: HedgeData[] = [];

        candidates.forEach(function(h) {
            const validation = this.validateHedgeForAcquire(h);
            if (validation.valid) {
                validHedges.push(h);
            } else {
                console.log('[Acquire] Skipping hedge', h.id, '(' + h.name + '):', validation.reason);
                skipped.push('"' + h.name + '" (' + validation.reason + ')');
            }
        }.bind(this));

        console.log('[Acquire] Valid hedges:', validHedges.length, '| Skipped:', skipped.length);

        if (validHedges.length === 0) {
            const toastMsg = skipped.length > 0
                ? 'No acquirable changes. Skipped: ' + skipped.join(', ')
                : 'No changes to acquire';
            console.log('[Acquire]', toastMsg);
            this._toastr.warning(toastMsg);
            return;
        }

        if (skipped.length > 0) {
            this._toastr.warning('Skipped: ' + skipped.join(', '));
        }

        // Build verification configs for all valid hedges
        const configs = validHedges.map((h) => {
            const comboItems = this.makeComboItems(h.id);
            const orderPx = this.hedgeMatrixDataService.getTransCost(h.id) / this.portfolioDefaultQty;

            const config: OrdersVerificationDialogConfig = {
                orderLegs: comboItems.sort((a, b) => b.ticker.localeCompare(a.ticker)),
                orderPrice: orderPx,
                defaultQty: this.portfolioDefaultQty,
                ledgerPrice: () => this.hedgeMatrixDataService.getTransCost(h.id),
                isNewHedge: h.isNew,
                hedgeName: h.isNew ? '' : undefined,
                hedgePlaceholder: h.isNew ? h.name : undefined,
                groupName: h.isNew ? undefined : h.name,
                groupColor: h.color,
            };
            return config;
        });

        const existingLabels = await this.getExistingHedgeLabels();
        console.log('[Acquire] Showing order verification dialog for', configs.length, 'hedges. existingLabels:', existingLabels);

        // CR-20260827-001: 4th arg opts in to the "Optimize Grouping" preview; the ATM
        // (CR-20260830-002) feeds the backend arrangement's skew rank.
        this.orderVerificationDialog.show(configs, 'acquire', existingLabels, true,
            (legs, qty, md) => this.pricingStepsPopup.show(legs, qty, md),
            this.getAtmStrike())
            .then(() => {
                console.log('[Acquire] Order verification accepted for all hedges');

                const messages = validHedges
                    .map((h, i) => {
                        const msg = this.makeComboMessage(h);
                        if (msg) {
                            msg.strategyName = h.isNew ? configs[i].hedgeName : h.name;
                            console.log('[Acquire] Combo message for hedge:', h.id, 'strategyName:', msg.strategyName,
                                'items:', msg.items?.length, 'isTransaction:', !!msg.hedgingGridTransaction);
                        }
                        return msg;
                    })
                    .filter(m => !isVoid(m));

                // Save position mapping before acquire so we can restore after reload
                validHedges.forEach((h, i) => {
                    if (h.isNew) {
                        this._acquiredHedgeIdMap.set(h.id, configs[i].hedgeName || h.name);
                    }
                });
                this.saveGridState();

                console.log('[Acquire] Total combo messages to emit:', messages.length);
                this.acquireRequested.emit(messages);
            })
            .catch(() => {
                console.log('[Acquire] Order verification cancelled by user');
            });
    }

    private async getExistingHedgeLabels(): Promise<string[]> {
        const positions = await this._hedgePositionsService.getHedgePositions(this.selectedPortfolio);
        return positions
            .map(function(p) { return p.label; })
            .filter(function(v, i, a) { return a.indexOf(v) === i; });
    }

    @DetectMethodChanges()
    showPricingSteps(hedgeId: string, legType: 'original' | 'trans' | 'outcome',
                     expiration?: string, side?: string) {

        let theLegs: TransactionLeg[] = [];

        let groupName = 'N/A';
        let groupColor = 'lemonchiffon';
        let selectedExpiration;
        let selectedLegType: any;

        let type;
        switch (legType) {
            case 'original':
                type = 'Original';
                break;
            case 'trans':
                type = 'Transaction';
                break;
            case 'outcome':
                type = 'Outcome';
                break;
        }

        selectedLegType = `(${type})`;

        if (hedgeId === 'total') {

            groupName = 'Expiration Total';

            if (!isVoid(side)) {
                groupName = `${side} Total`;
            } else {
                if (isVoid(expiration)) {
                    groupName = 'Grand Total';
                }
            }

            if (!isVoid(expiration)) {
                selectedExpiration = makeFullExpirationDate(expiration);
            }

            if (legType === 'trans') {

                const hedges = this.hedgeMatrixDataService
                    .getHedges().filter(x => x.isNew || x.underModification);

                let allLegs = hedges.flatMap(x => this.hedgeMatrixDataService.getTransactionLegs(x.id));

                if (!isVoid(expiration)) {
                    allLegs = allLegs.filter(x => x.ticker.indexOf(expiration) !== -1);
                }

                if (!isVoid(side)) {
                    side = side.toLowerCase() === 'call' ? 'Call' : 'Put';
                    allLegs = allLegs.filter(x => x.ticker.indexOf(side) !== -1);
                }

                theLegs = allLegs;

            } else if (legType === 'original') {

                const hedges = this
                    .hedgeMatrixDataService
                    .getHedges();

                let allLegs = hedges
                    .flatMap(x => this.hedgeMatrixDataService.getOriginalLegs(x.id));

                if (!isVoid(expiration)) {
                    allLegs = allLegs.filter(x => x.ticker.indexOf(expiration) !== -1);
                }

                if (!isVoid(side)) {
                    side = side.toLowerCase() === 'call' ? 'Call' : 'Put';
                    allLegs = allLegs.filter(x => x.ticker.indexOf(side) !== -1);
                }

                theLegs = allLegs;

            } else if (legType === 'outcome') {
                const hedges = this.hedgeMatrixDataService
                    .getHedges();

                let allLegs = hedges
                    .flatMap(x => this.hedgeMatrixDataService.getNetOutcomeLegs(x.id));

                if (!isVoid(expiration)) {
                    allLegs = allLegs.filter(x => x.ticker.indexOf(expiration) !== -1);
                }

                if (!isVoid(side)) {
                    side = side.toLowerCase() === 'call' ? 'Call' : 'Put';
                    allLegs = allLegs.filter(x => x.ticker.indexOf(side) !== -1);
                }

                theLegs = allLegs;
            }

            theLegs = theLegs.map(leg => {
                const transLeg: TransactionLeg = {
                    ticker: leg.ticker,
                    qty: leg.qty,
                    priceAsOwned: legType !== 'trans'
                };
                return transLeg;
            });

        } else {

            const hedgeData = this._hedgeMatrixDataService.getHedge(hedgeId);

            if (!isVoid(hedgeData)) {

                groupName = hedgeData.name;
                groupColor = hedgeData.color;

                let legs: HedgeMatrixTransLeg[] = [];

                if (legType === 'trans') {
                    legs = this._hedgeMatrixDataService.getTransactionLegs(hedgeId);
                } else if (legType === 'outcome') {
                    legs = this._hedgeMatrixDataService.getOutcomeLegs(hedgeId);
                } else if (legType === 'original') {
                    legs = this._hedgeMatrixDataService.getOriginalLegs(hedgeId);
                }

                theLegs = legs.map(leg => {
                    const transLeg: TransactionLeg = {
                        ticker: leg.ticker,
                        qty: leg.qty,
                        priceAsOwned: legType !== 'trans',
                    };
                    return transLeg;
                });
            }
        }

        const groupMetadata: GroupMetadata = {
            name: groupName,
            color: groupColor,
            expiration: selectedExpiration,
            legType: selectedLegType
        }

        this.pricingStepsPopup.show(
            theLegs,
            this.portfolioDefaultQty,
            groupMetadata
        );
    }

    @DetectMethodChanges()
    toggleColumnsAutosize() {
        this.isLoading = true;
        setTimeout(() => {
            try {
                if (this._sizeColumnsButtonText === FIT_WIDTH_COLUMNS_BTN_TEXT) {
                    this.theGrid.columnApi.autoSizeAllColumns();
                    this._sizeColumnsButtonText = OPT_WIDTH_COLUMNS_BTN_TEXT;
                } else if (this._sizeColumnsButtonText === OPT_WIDTH_COLUMNS_BTN_TEXT) {
                    this._sizeColumnsButtonText = FIT_WIDTH_COLUMNS_BTN_TEXT;
                    this.theGrid.columnApi
                        .getAllColumns()
                        .filter(x => isHedgeColumn(x.getColId()))
                        .forEach((column) => {
                            const hedgeId = getHedgeIdFromColId(column.getColId());
                            const cellData = this.hedgeMatrixDataService.getCellDataForHedge(hedgeId);
                            if (isVoid(cellData)) return;

                            const maxQty = Math.max(...cellData.map(x => Math.abs(x.qty || 0)));
                            const maxTransQty = Math.max(...cellData.map(x => Math.abs(x.transQty || 0)));
                            const maxOutcomeQty = Math.max(...cellData.map(x => Math.abs(x.outcomeQty|| 0)));

                            const highestQty = Math.max(maxQty, maxTransQty, maxOutcomeQty);

                            const legPrices = cellData.map(x => this._lastQuoteCache.getLastQuote(x.ticker)?.mid || 0);
                            const highestPrice = Math.max(...legPrices);

                            let theWidth = Width_FitDate;

                            const is6digit = highestQty >= 100 && highestPrice >= 100;
                            const is3digit = (highestQty >= 100) !== (highestPrice >= 100);

                            if (is3digit) {
                                theWidth = Width_Fit3digit;
                            }

                            if (is6digit) {
                                theWidth = Width_Fit6digit;
                            }

                            const isEsHedge = this.nonBaseUnderlyingSections?.some(
                                s => s.hedgeList?.some(h => h.id === hedgeId)
                            );
                            if (isEsHedge) {
                                theWidth += 55;
                            }

                            this.theGrid.columnApi.setColumnWidth(column, theWidth);
                        });
                }
            } finally {
                this.isLoading = false;
                this._changeDetector.detectChanges();
            }
        });

    }

    onCollapsedStateChanged(matrixSectionCollapsed: boolean) {
        if (matrixSectionCollapsed) {
            return;
        }

        setTimeout(() => this.theGrid.columnApi.autoSizeAllColumns());
    }
}
